export type ServicePillar = {
  title: string;
  body: string;
  outputs: string[];
};

export type ServiceCadence = {
  cadence: string;
  items: string[];
};

export type ServicePhase = {
  phase: string;
  when: string;
  work: string[];
};

export type ServiceTier = {
  tier: string;
  price: string;
  cadence: string;
  includes: string[];
  best: string;
};

export type ServiceFAQ = { q: string; a: string };

export type ServicePage = {
  slug: string;
  category: string;
  title: string;
  metaTitle: string;
  metaDescription: string;
  eyebrow: string;
  hero: {
    headline: string;
    deck: string;
  };
  tldr: string[];
  overview: string[];
  philosophy: {
    heading: string;
    body: string[];
  };
  pillars: ServicePillar[];
  framework: {
    name: string;
    body: string[];
    steps: { label: string; body: string }[];
  };
  deliverables: ServiceCadence[];
  timeline: ServicePhase[];
  investment: ServiceTier[];
  fitFor: string[];
  notFor: string[];
  relatedCaseStudies: string[]; // portfolio slugs
  faqs: ServiceFAQ[];
  related: string[]; // service slugs
};

export const SERVICES: ServicePage[] = [
  /* ============================ LOCAL SEO ============================ */
  {
    slug: "local-seo",
    category: "Search",
    title: "Local SEO",
    metaTitle: "Local SEO Austin — Attract More Local Enquiries | Optimize Plus",
    metaDescription:
      "Austin local SEO services aligning service-area pages, Google Business Profile and reviews with real customer demand. Book a Strategy Call.",

    eyebrow: "Austin Local SEO · Map Pack",
    hero: {
      headline: "Local SEO Austin: connect local searches with your services.",
      deck: "For Austin service-area businesses, local SEO connects useful service pages, accurate Google Business Profile information and genuine customer feedback. Optimize Plus builds a strategy around your actual coverage, competition and priority services, then tracks local visibility and qualified enquiries separately. Explore our location pages for market-specific coverage across Central Texas.",
    },
    tldr: [
      "Service-area page systems targeting city × service combinations",
      "Google Business Profile overhauls with weekly posting cadence",
      "Review generation flows and reputation monitoring",
      "LocalBusiness / Service schema on every relevant page",
      "Monthly local pack tracking with sourced BrightLocal + GSC data",
      "Neighborhood-level content clusters for Austin micro-markets",
    ],
    overview: [
      "Local pack results receive 44% of clicks on local intent queries and appear above traditional organic — so for any service-area business, ranking in the map is a bigger lever than ranking blue links.",
      "But the pack is unforgiving. Google evaluates dozens of signals — proximity, prominence, relevance, review velocity, category alignment, GBP completeness, on-page relevance — and shuffles them constantly. In dense metros like Austin, a business ranking fourth is functionally invisible.",
      "Our Local SEO programme runs the disciplines that move the pack together: on-page (service-area pages), off-page (citations and links), profile (GBP), and social proof (reviews). Then we measure with GSC and BrightLocal, weekly.",
    ],
    philosophy: {
      heading: "Local SEO is a system, not a checklist.",
      body: [
        "Most local SEO vendors sell a monthly checklist: 20 citations, 4 GBP posts, 3 blog articles. The checklist never fails and the rankings never change. This is the definition of insanity in a shrinking map pack.",
        "We treat local as a growth engine. Every city gets a service-area page with genuinely useful local context. Every service gets a landing page that ranks on its own commercial intent. The GBP is treated as a product surface, not a listing. We focus on velocity and recency, because the map pack rewards active signals.",
      ],
    },
    pillars: [
      {
        title: "Service-area pages",
        body: "The single highest-leverage asset in local SEO. Each city × service combination gets a page that ranks on its own — with local landmarks, service radius, pricing signals, and local FAQs.",
        outputs: ["City × service matrix", "Local landmark & radius signals", "Local review embeds", "Schema per service area"],
      },
      {
        title: "Google Business Profile",
        body: "The GBP is your storefront on the map. We rebuild categories, services, service areas, photos, and posting cadence — then keep it fresh weekly.",
        outputs: ["Category + service audit", "Geo-tagged photo library", "Weekly post cadence", "Q&A seeding + monitoring"],
      },
      {
        title: "Review generation",
        body: "Review velocity is a ranking signal and a conversion signal. We build a review flow — SMS + email — and monitor sentiment monthly.",
        outputs: ["Automated review request flow", "Response templates for owners", "Monthly sentiment report", "Bad-review recovery playbook"],
      },
      {
        title: "Citations & local links",
        body: "Consistent NAP data across the local citation graph is table stakes. Local links from chambers, associations, and press are the moat.",
        outputs: ["NAP consistency audit", "Priority citation build", "Local link prospecting", "Chamber & association outreach"],
      },
    ],
    framework: {
      name: "The Local Growth Loop",
      body: [
        "Every engagement runs the same closed loop each week: audit → build → publish → monitor → refresh. The loop compounds because every city page, every GBP post, and every earned review adds to the moat.",
      ],
      steps: [
        { label: "Audit", body: "GBP, citations, on-page, reviews, competitors — logged and prioritised." },
        { label: "Build", body: "Ship service-area pages, GBP updates, schema, and review flow." },
        { label: "Publish", body: "Weekly GBP posts, photos, and answers to seeded Q&A." },
        { label: "Monitor", body: "Track pack position, GSC clicks, review velocity, competitor moves." },
        { label: "Refresh", body: "Update the pages losing position; expand the ones gaining." },
      ],
    },
    deliverables: [
      {
        cadence: "Weekly",
        items: ["GBP posts (services / offers / photos)", "Rank tracking snapshot", "Review request cycle", "Competitor pack watch"],
      },
      {
        cadence: "Monthly",
        items: ["Local dashboard report (GSC + BrightLocal)", "1–2 new service-area pages", "Citation build (10–20)", "Review sentiment analysis"],
      },
      {
        cadence: "Quarterly",
        items: ["Local competitive audit", "GBP category recalibration", "Local link prospecting sprint", "Executive local review"],
      },
    ],
    timeline: [
      { phase: "Foundation", when: "Weeks 1–2", work: ["Local audit", "GBP category + service audit", "NAP consistency sweep", "Service-area page IA"] },
      { phase: "Build", when: "Weeks 3–8", work: ["Service-area page rollout", "GBP overhaul + schema", "Review flow launch", "Priority citations"] },
      { phase: "Compound", when: "Months 3–6", work: ["Weekly posting cadence", "Ongoing service-area expansion", "Local link outreach", "Pack position tracking"] },
      { phase: "Defend", when: "Month 6+", work: ["Refresh top pages", "Recover lost pack positions", "Expand to adjacent service areas"] },
    ],
    investment: [
      {
        tier: "Local Focused",
        price: "$2,400 / mo",
        cadence: "1 location · 5 services",
        includes: ["Weekly GBP cadence", "Monthly service-area page", "Monthly report", "Review flow"],
        best: "Single-location service businesses starting fresh.",
      },
      {
        tier: "Local Growth",
        price: "$4,800 / mo",
        cadence: "Up to 3 locations",
        includes: ["Everything in Focused", "2 new pages / mo", "Citation build + local links", "Quarterly business review"],
        best: "Multi-location operators or single locations in competitive metros.",
      },
      {
        tier: "Local Enterprise",
        price: "$9,000+ / mo",
        cadence: "10+ locations",
        includes: ["Everything in Growth", "Franchise / multi-brand governance", "Dedicated local strategist", "Custom Looker dashboard"],
        best: "Franchise systems and regional multi-location brands.",
      },
    ],
    fitFor: [
      "Service-area businesses (HVAC, plumbing, auto, legal, medical, home services)",
      "Multi-location operators consolidating their local presence",
      "Franchise systems with corporate + local governance needs",
      "Owners who understand local reviews are a product, not a checklist",
    ],
    notFor: [
      "Businesses without a physical service area or storefront",
      "Pure e-commerce brands (see e-commerce SEO instead)",
      "Owners looking for 'more citations' as the primary metric",
    ],
    relatedCaseStudies: ["air-tech-pro-hvac", "carage-auto", "foreign-affairs-auto"],
    faqs: [
      { q: "How long until we rank in the local pack?", a: "Fresh GBPs with a clean foundation can see pack impressions in 4–6 weeks. Sustained top-3 positions in competitive metros typically take 4–6 months of the full loop." },
      { q: "Do we need a physical office in every city we want to rank in?", a: "Not for service-area businesses. Google allows service-area configuration without a storefront, but proximity still matters — expect stronger performance closer to your registered address." },
      { q: "How many reviews do we need?", a: "Reviews are relative to your local competitors. We benchmark you against the top 5 in your pack and set a monthly velocity target — usually 4–12 new reviews per month per location." },
      { q: "Can you help fix a suspended GBP?", a: "Yes. GBP reinstatement is a specialty; we handle the appeal, evidence packet, and follow-up. Timelines vary from 3 days to 8 weeks depending on the suspension reason." },
      { q: "What if we already have a lot of citations?", a: "Great — we audit them for consistency and prune the ones actively hurting you. We rarely recommend building more citations after the top 30 unless there's a specific competitive gap." },
    ],
    related: ["technical-seo", "google-business-profile", "content-strategy"],
  },

  /* ============================ TECHNICAL SEO ============================ */
  {
    slug: "technical-seo",
    category: "Search",
    title: "Technical SEO",
    metaTitle: "Technical SEO Austin — Fix Crawl & Indexing | Optimize Plus",
    metaDescription:
      "Technical SEO services in Austin: crawl access, indexing, canonicals, Core Web Vitals and structured data. Prioritised fixes. Book a Strategy Call.",
    eyebrow: "Technical SEO · Site Health",
    hero: {
      headline: "Technical SEO in Austin that fixes the reasons your rankings won't move.",
      deck: "Optimize Plus runs the audit, owns the fix log, and ships the changes — from Core Web Vitals engineering to JavaScript rendering to schema to Shopify and WordPress migrations. This is the discipline every Austin SEO programme depends on, delivered by a Central Texas team that ships fixes, not tickets. We specialize in resolving the technical debt that keeps Austin SMBs stuck on page two.",
    },
    tldr: [
      "Full technical audit: crawl, indexation, JS rendering, canonical, CWV",
      "Prioritised fix log with owner, effort, and expected impact",
      "Schema + entity signalling on every relevant template",
      "Core Web Vitals engineering (LCP, CLS, INP)",
      "Migration planning and The Migration Shield™ governance",
      "Entity graph mapping and stable @id schema deployment",
    ],
    overview: [
      "Technical SEO is the discipline where a single misconfigured directive can cancel six months of content and link work. And where a single well-designed schema can unlock rich results and AI citations that compound for years.",
      "Most sites don't have a content problem or a link problem. They have a technical problem masquerading as one. Duplicated URLs eat ranking signals. Blocked JavaScript hides the content from Googlebot. A 3.2-second LCP suppresses every page's ceiling. We resolve these bottlenecks so your content can actually reach its intended audience.",
      "We run the audit, hand you a fix log with owner and priority, and ship the changes ourselves when the client engineering team is at capacity.",
    ],
    philosophy: {
      heading: "We refuse to build content or links onto a broken foundation.",
      body: [
        "If the crawl isn't clean, no amount of content will rank. If the render is broken, links point to blank pages. If CWV is failing, the ceiling on every URL is capped. We treat technical SEO as a prerequisite, not an add-on.",
        "Technical SEO isn't optional; it's the enabler. Every engagement starts here — even when the client only asked for content. We believe in stable entity signals and machine-readable clarity as the path to AEO/GEO success.",
      ],
    },
    pillars: [
      {
        title: "Crawl & indexation",
        body: "We map every URL, canonical, redirect, and directive — then produce a fix log that resolves duplicate signals and unlocks the pages that should be indexed.",
        outputs: ["Full site crawl (Screaming Frog + Sitebulb)", "Indexation coverage audit", "Canonical + redirect map", "Robots + sitemap governance"],
      },
      {
        title: "JS rendering",
        body: "Modern SPAs and hybrid rendering leak SEO value. We audit how Googlebot sees your site vs. how users do, and specify the rendering strategy that closes the gap.",
        outputs: ["Rendered-vs-source audit", "SSR / SSG / hydration recommendation", "JS budget analysis", "Prerender fallback specs"],
      },
      {
        title: "Core Web Vitals",
        body: "LCP, CLS, and INP are ranking factors and conversion factors. We engineer them — not just audit them — with a prioritised remediation plan.",
        outputs: ["CWV field + lab audit (CrUX + Lighthouse)", "LCP element optimisation plan", "CLS diagnosis + fix", "Image + font strategy"],
      },
      {
        title: "Schema & entity",
        body: "Structured data is how you get rich results, sitelinks, and (increasingly) AI citations. We deploy the schema types that match your business — and validate them.",
        outputs: ["Schema strategy per template", "Organization / LocalBusiness / Product / Article", "Validation via schema.org + GSC", "FAQPage + HowTo + Video where relevant"],
      },
      {
        title: "Migrations",
        body: "Redesigns, replatforms, and consolidations are where SEO value is most often destroyed. We plan the migration so nothing is lost.",
        outputs: ["Migration risk map", "Redirect matrix", "Pre / during / post checklist", "Post-launch monitoring"],
      },
    ],
    framework: {
      name: "The Technical Debt Ledger",
      body: [
        "Every audit produces a ledger: issue, severity, owner, effort, expected impact. We work top-to-bottom, revisit weekly, and never mark an issue closed without verifying the fix in production.",
      ],
      steps: [
        { label: "Log", body: "Every issue captured with severity and evidence." },
        { label: "Own", body: "Every issue assigned to a person and a sprint." },
        { label: "Ship", body: "Fixes deployed and verified in a staging environment first." },
        { label: "Verify", body: "Production check, GSC monitoring, and re-crawl to confirm resolution." },
      ],
    },
    deliverables: [
      { cadence: "Weekly", items: ["CWV monitoring", "GSC indexation watch", "New-issue triage", "Fix-log update"] },
      { cadence: "Monthly", items: ["Full site re-crawl", "Schema QA sweep", "Log-file sample analysis", "Technical health report"] },
      { cadence: "Quarterly", items: ["Deep-dive technical audit", "Migration readiness review", "Rendering strategy refresh", "Competitive technical benchmark"] },
    ],
    timeline: [
      { phase: "Foundation audit", when: "Weeks 1–3", work: ["Full crawl + render diff", "Indexation + canonical map", "Schema audit", "CWV baseline"] },
      { phase: "Priority fixes", when: "Weeks 4–8", work: ["Top-severity fix rollout", "Schema deployment", "CWV engineering sprint"] },
      { phase: "Compound", when: "Months 3–6", work: ["Ongoing crawl monitoring", "Template-level improvements", "Log-file signal analysis"] },
      { phase: "Prevent", when: "Ongoing", work: ["Migration governance", "Release checklist", "Quarterly deep audits"] },
    ],
    investment: [
      { tier: "Audit + Fix Log", price: "$6,500 one-time", cadence: "3-week engagement", includes: ["Full audit", "Prioritised fix log", "1-hour handoff call", "30 days of follow-up support"], best: "Teams with capable engineering that need a diagnosis." },
      { tier: "Technical Retainer", price: "$4,200 / mo", cadence: "Ongoing", includes: ["Everything in Audit", "Monthly re-crawl + report", "Fix implementation support", "CWV monitoring"], best: "Sites with active development where ongoing hygiene matters." },
      { tier: "Migration", price: "From $12,000", cadence: "Project-based", includes: ["Migration risk map", "Redirect matrix", "Pre / during / post support", "60-day post-launch monitoring"], best: "Redesigns, replatforms, and site consolidations." },
    ],
    fitFor: [
      "Sites where rankings have flatlined despite good content",
      "Post-migration recovery and pre-migration planning",
      "SPAs, headless CMSes, and hybrid rendering setups",
      "Teams that value 'ship the fix' over 'file the ticket'",
    ],
    notFor: [
      "Brand-new sites with no content — start with content strategy first",
      "Teams looking for a report to file rather than a fix to ship",
    ],
    relatedCaseStudies: ["foreign-affairs-auto", "lynx-systems"],
    faqs: [
      { q: "Do you implement fixes or just recommend them?", a: "Both. We're comfortable in WordPress, Next.js, Astro, Shopify, and most headless setups. When there's a client engineering team, we hand off; when there isn't, we ship." },
      { q: "How long does a technical audit take?", a: "Three weeks from kickoff to handoff. Week 1 crawl + render diff, week 2 schema + CWV, week 3 log-file analysis + fix-log finalisation." },
      { q: "Can you fix Core Web Vitals on WordPress?", a: "Yes. Most of the LCP gains on WordPress come from image strategy, font loading, and cache configuration — none of which require rebuilding the theme. When we do need theme work, we scope it separately." },
      { q: "Do you handle migrations?", a: "It's one of our specialties. We've run migrations from WordPress to headless, Shopify replatforms, and multi-site consolidations. Redirect matrix + pre/during/post monitoring is non-negotiable." },
      { q: "What tools do you use?", a: "Screaming Frog, Sitebulb, GSC, GA4, Semrush, Ahrefs, log-file tools (Splunk / self-hosted), Lighthouse CI, CrUX, and Schema.org validators." },
    ],
    related: ["seo", "aeo-geo", "web-design"],
  },

  /* ============================ CONTENT STRATEGY ============================ */
  {
    slug: "content-strategy",
    category: "Search",
    title: "Content Strategy & topical authority",
    metaTitle: "Content Strategy Austin — Topical Authority for AI Search",
    metaDescription:
      "Austin content strategy agency building topical authority that ranks on Google and gets cited by ChatGPT & Perplexity. Book a free audit.",
    eyebrow: "Editorial System · Austin, TX",
    hero: {
      headline: "Content strategy that builds Austin topical authority — not publishing noise.",
      deck: "Cluster maps, editorial calendars, commercial-intent briefs, and a refresh cycle that keeps every page compounding for Austin, Round Rock, Cedar Park, and Central Texas buyers. Designed to rank on Google — and be cited by ChatGPT, Perplexity, Gemini, and Google AI Overviews. We focus on owning the topic, not just ranking the keyword.",
    },
    tldr: [
      "Topical cluster map anchored to commercial intent",
      "Editorial calendar with owner, priority, and target keyword per brief",
      "Long-form briefs designed for humans, Googlebot, and answer engines",
      "Refresh cycle for the top 20% of pages driving 80% of traffic",
      "Governance model so quality doesn't degrade at scale",
      "Entity-first writing protocols for AEO/GEO citation readiness",
    ],
    overview: [
      "Most 'content strategies' are a publishing calendar with a spreadsheet of keywords. That produces posts. It doesn't produce topical authority. We treat content as a cluster problem, not a page problem.",
      "We build cluster maps — hubs and spokes anchored to commercial intent — with an editorial calendar that respects both the ranking timeline and the refresh cadence. Every brief targets a specific keyword with a specific SERP intent match.",
      "The result: content that ranks, gets internally linked correctly, gets cited in AI answers, and doesn't require a rewrite in six months.",
    ],
    philosophy: {
      heading: "Publish less. Own more.",
      body: [
        "Publishing more posts than your competitor doesn't win — publishing better ones on the right topics does. The best content strategies compound because each new page is an asset, not just a URL. We follow The Compounding Content Loop™.",
        "We'd rather ship one 3,000-word cornerstone that ranks for 200 keywords than 20 thin posts that rank for none. And we'd rather refresh a page that already ranks than write a new one that competes with it. We believe in entity clarity over keyword density.",
      ],
    },
    pillars: [
      {
        title: "Cluster mapping",
        body: "Every content programme starts with a hub-and-spoke map — commercial hubs, informational spokes, and clear internal linking rules.",
        outputs: ["Cluster architecture", "Hub + spoke page briefs", "Internal linking rules", "Priority order by commercial value"],
      },
      {
        title: "Editorial calendar",
        body: "A living calendar with owner, priority, target keyword, and target intent per brief — updated weekly.",
        outputs: ["12-month calendar", "Weekly sprint list", "Owner + due date", "Publish + refresh cadence"],
      },
      {
        title: "Briefs & production",
        body: "Long-form briefs written for humans first, structured for search engines and citation engines second.",
        outputs: ["Intent-matched briefs", "Entity checklist", "Internal link plan per brief", "Media / schema requirements"],
      },
      {
        title: "Refresh cycle",
        body: "The top 20% of your pages generate 80% of traffic. We refresh them on a rolling cadence so they keep gaining position.",
        outputs: ["Top-pages inventory", "Quarterly refresh sprint", "Cannibalisation audit", "Retirement / redirect plan"],
      },
    ],
    framework: {
      name: "The Compounding Content Loop",
      body: [
        "Every quarter runs the loop: map → prioritise → produce → publish → measure → refresh. We prefer refreshing to producing whenever the ranking opportunity is on an existing URL.",
      ],
      steps: [
        { label: "Map", body: "Cluster architecture anchored to commercial intent." },
        { label: "Prioritise", body: "Sequence briefs by expected revenue impact." },
        { label: "Produce", body: "Long-form briefs → drafts → editorial review → publish." },
        { label: "Refresh", body: "Update top pages before starting new ones." },
      ],
    },
    deliverables: [
      { cadence: "Weekly", items: ["1–2 published pieces", "Editorial calendar update", "Brief review + QA"] },
      { cadence: "Monthly", items: ["Cluster health report", "Refresh sprint (3–5 pages)", "Internal linking sweep", "Top-pages performance report"] },
      { cadence: "Quarterly", items: ["Cluster map recalibration", "Cannibalisation audit", "Content pruning + redirect plan", "Executive editorial review"] },
    ],
    timeline: [
      { phase: "Strategy", when: "Weeks 1–3", work: ["Cluster map", "Brief templates", "Editorial calendar Q1", "Governance model"] },
      { phase: "Production", when: "Weeks 4–12", work: ["First 8–12 briefs published", "Internal linking rollout", "First refresh sprint"] },
      { phase: "Compound", when: "Months 4–9", work: ["Ongoing publish + refresh", "Cluster expansion", "Cannibalisation cleanup"] },
      { phase: "Governance", when: "Ongoing", work: ["Quarterly editorial reviews", "Style guide maintenance", "Author onboarding"] },
    ],
    investment: [
      { tier: "Strategy Only", price: "$8,500 one-time", cadence: "4-week engagement", includes: ["Cluster map", "Editorial calendar", "Brief templates", "Governance model"], best: "Teams with in-house writers who need the strategy layer." },
      { tier: "Content Retainer", price: "$5,500 / mo", cadence: "Ongoing", includes: ["4–6 pieces / mo", "Refresh cycle", "Monthly report", "Cluster governance"], best: "Brands committing to a real editorial programme." },
      { tier: "Publication Scale", price: "$12,000+ / mo", cadence: "Ongoing", includes: ["12+ pieces / mo", "Managed editorial team", "Custom analytics", "Weekly leadership review"], best: "Publications and enterprises with a real content velocity target." },
    ],
    fitFor: [
      "Brands that treat content as an asset, not a campaign",
      "Teams with in-house SMEs who can be interviewed for authority signals",
      "Sites with a foundation of technical SEO already in place",
    ],
    notFor: [
      "Teams looking for AI-spun content at scale",
      "Brands unwilling to invest in refresh cycles or governance",
    ],
    relatedCaseStudies: ["foreign-affairs-auto", "carage-auto"],
    faqs: [
      { q: "Do you use AI in your writing?", a: "For research, outlining, and QA — yes. For final prose on published pages — no. Every piece has a human author and a human editor." },
      { q: "How long does content take to rank?", a: "New content typically starts showing GSC impressions in 4–8 weeks and reaches position 1–3 in 4–9 months depending on difficulty and internal link authority." },
      { q: "Do you refresh old content?", a: "Yes — refresh work is often higher ROI than net-new. We refresh on a quarterly cadence based on ranking opportunity scoring." },
      { q: "Can you interview our subject matter experts?", a: "Yes. Expert interviews are the fastest way to produce E-E-A-T-strong content. We handle the interviews and turn transcripts into publishable long-form." },
      { q: "How do you handle topical authority?", a: "Cluster maps with strict internal linking rules. Every spoke links to its hub; every hub links to its most valuable spokes. Topical authority is a graph problem, not a word-count problem." },
    ],
    related: ["seo", "aeo-geo", "link-building"],
  },

  /* ============================ CONTENT MARKETING ============================ */
  {
    slug: "content-marketing",
    category: "Search",
    title: "Content Marketing Services",
    metaTitle: "Content Marketing Services — SEO & AI-Cited Content | Optimize Plus",
    metaDescription:
      "Content marketing services for SMBs and B2B brands: strategy, production, distribution, and refresh — engineered to rank on Google and earn citations in ChatGPT, Perplexity, and Google AI Overviews.",
    eyebrow: "Full-Funnel Content Programme · Austin, TX",
    hero: {
      headline: "Content marketing that ranks, converts, and gets cited by AI Overviews.",
      deck: "Strategy, expert-led production, distribution, and refresh cycles — for SMBs and B2B brands that want content to work as an owned demand channel, not a publishing hobby. Built to win on Google and be cited by ChatGPT, Perplexity, Gemini, and Google AI Overviews.",
    },
    tldr: [
      "Full-funnel programme: TOFU, MOFU, and BOFU covered — not just blog posts",
      "Expert-led production with SME interviews for real E-E-A-T signals",
      "Every asset engineered for both Googlebot and answer engines (AEO/GEO)",
      "Distribution built in — email, LinkedIn, syndication, and internal linking",
      "Refresh cycle on the top 20% of pages driving 80% of revenue",
    ],
    overview: [
      "Most content marketing programmes stall because they're built around a publishing calendar instead of a demand system. A blog post per week doesn't generate pipeline — a full-funnel content system does.",
      "We run content marketing as a compounding asset: strategy grounded in commercial intent, production led by your subject matter experts, distribution baked into the workflow, and measurement tied to revenue — not vanity metrics.",
      "Because we're an SEO-native agency, every asset is engineered from the first outline to earn organic rankings and get cited in AI Overviews, ChatGPT, and Perplexity. That's how content marketing becomes the lowest-CAC channel on your P&L.",
    ],
    philosophy: {
      heading: "Content marketing is a demand system, not a publishing quota.",
      body: [
        "The best content marketing programmes we've seen ship less and earn more. They pick topics tied to revenue, invest in real subject-matter depth, distribute deliberately, and refresh what already works. That's the model we run.",
        "Our unfair advantage is the SEO and AI-search side of the house. Every piece of content we produce is designed to rank on Google AND to be the source AI Overviews cite when a buyer asks the answer engine. That dual optimisation is where most content teams leave 60%+ of demand on the table.",
      ],
    },
    pillars: [
      {
        title: "Strategy & positioning",
        body: "Full-funnel content strategy anchored to ICP, buyer stage, and commercial intent — not keyword volume alone.",
        outputs: ["ICP & buyer-stage map", "Funnel-aware content plan", "Editorial pillars & POV", "Commercial-intent priority list"],
      },
      {
        title: "Expert-led production",
        body: "SME interviews, original research, and long-form assets that carry real E-E-A-T weight — the kind Google and AI engines reward.",
        outputs: ["SME interview production", "Long-form articles & guides", "Original research & data assets", "Video & podcast repurposing"],
      },
      {
        title: "AEO / GEO optimisation",
        body: "Structure, schema, and answer-block writing designed to be the source AI Overviews and ChatGPT cite in your category.",
        outputs: ["Answer-block briefs", "FAQ + HowTo schema", "Citation-worthy statistics & quotes", "AI-visibility monitoring"],
      },
      {
        title: "Distribution & repurposing",
        body: "Every hero asset gets a distribution plan — email, LinkedIn, syndication, sales enablement — so it earns compounding attention.",
        outputs: ["Newsletter cadence", "LinkedIn distribution kit", "Sales enablement one-pagers", "Podcast + video repurposing"],
      },
      {
        title: "Refresh & governance",
        body: "The top 20% of pages generate 80% of leads. We refresh, re-optimise, and consolidate before we produce anything new.",
        outputs: ["Top-pages inventory", "Quarterly refresh sprint", "Cannibalisation audit", "Retire / redirect plan"],
      },
    ],
    framework: {
      name: "The Compounding Content Loop™",
      body: [
        "A proprietary methodology designed to ensure every asset published becomes a long-term equity-building asset. We focus on a high-velocity feedback loop between research, production, and distribution, with a mandatory 25% allocation to content refresh to maintain peak visibility.",
      ],
      steps: [
        { label: "Research", body: "Identifying search intent gaps and AI citation opportunities using the GEO Success Matrix™." },
        { label: "Produce", body: "Developing SME-led, entity-rich content that satisfies both user intent and LLM extractability requirements." },
        { label: "Distribute", body: "Executing a multi-channel distribution plan to build early authority signals and social proof." },
        { label: "Refresh", body: "Quarterly re-optimization of high-performing assets to defend rankings and capture new AI search surfaces." },
      ],
    },
    deliverables: [
      { cadence: "Weekly", items: ["1–2 published assets", "Editorial standup + QA", "Distribution sends (email/LinkedIn)", "AI-visibility spot checks"] },
      { cadence: "Monthly", items: ["Content performance report", "Refresh sprint (3–5 pages)", "Internal linking sweep", "AI-Overview citation tracker", "Sales enablement update"] },
      { cadence: "Quarterly", items: ["Content strategy recalibration", "Original research or data asset", "Cannibalisation + pruning audit", "Executive editorial review"] },
    ],
    timeline: [
      { phase: "Foundation", when: "Weeks 1–4", work: ["ICP & buyer-stage map", "Editorial pillars & POV", "Cluster + calendar for Q1", "Brief templates & governance"] },
      { phase: "Production", when: "Weeks 5–12", work: ["First 8–12 hero assets published", "Distribution kits shipped", "Answer-block + schema rollout", "First refresh sprint"] },
      { phase: "Compound", when: "Months 4–9", work: ["Ongoing publish + refresh", "Original research asset", "AI-Overview citation expansion", "Sales enablement rollout"] },
      { phase: "Governance", when: "Ongoing", work: ["Quarterly editorial reviews", "Style guide maintenance", "SME interview cadence", "Attribution + reporting"] },
    ],
    investment: [
      { tier: "Content Starter", price: "$4,500 / mo", cadence: "Ongoing", includes: ["3 hero assets / mo", "Distribution kit per asset", "Monthly reporting", "Quarterly refresh"], best: "SMBs launching or restarting a content programme." },
      { tier: "Growth Retainer", price: "$8,500 / mo", cadence: "Ongoing", includes: ["6 hero assets / mo", "AEO/GEO optimisation", "Full distribution stack", "Refresh cycle + governance"], best: "B2B brands running content as a primary demand channel." },
      { tier: "Publication Scale", price: "$15,000+ / mo", cadence: "Ongoing", includes: ["12+ assets / mo", "Original research quarterly", "Managed editorial team", "Weekly leadership review"], best: "Category leaders committed to owning the topic." },
    ],
    fitFor: [
      "SMBs and B2B brands treating content as a demand channel",
      "Teams with in-house SMEs willing to be interviewed",
      "Brands committed to distribution — not just publishing",
    ],
    notFor: [
      "Teams looking for AI-spun content at scale",
      "Brands unwilling to invest in refresh cycles or SME time",
    ],
    relatedCaseStudies: ["foreign-affairs-auto", "carage-auto"],
    faqs: [
      { q: "How is this different from your Content Strategy service?", a: "Content Strategy is the editorial system and cluster map. Content Marketing is the full-funnel programme — strategy plus production, distribution, and refresh — run as a managed retainer with a monthly output commitment." },
      { q: "Do you write with AI?", a: "For research, outlining, and QA — yes. For final prose — no. Every published asset has a human SME source and a human editor. That's what earns citations in AI Overviews; AI-spun content rarely does." },
      { q: "How do you get content cited in AI Overviews and ChatGPT?", a: "Answer-block writing, entity-rich structure, FAQ / HowTo schema, and citation-worthy statistics or quotes. We monitor AI-visibility monthly and refresh briefs when the model behaviour shifts." },
      { q: "How long until content marketing pays back?", a: "First GSC impressions in 4–8 weeks. First pipeline attribution typically in months 3–5. Compounding phase — where CAC drops materially — usually months 6–12." },
      { q: "Do you handle distribution or just production?", a: "Both. Every hero asset ships with a distribution kit: newsletter copy, LinkedIn posts, sales enablement one-pager, and repurposing plan. Production without distribution is where most programmes stall." },
      { q: "Can you work with our in-house team?", a: "Yes — we frequently plug into in-house content teams as the SEO / AEO / distribution layer, or run the full programme end-to-end. Both engagement models are supported." },
    ],
    related: ["content-strategy", "aeo-geo", "digital-pr", "link-building"],
  },


  /* ============================ LINK BUILDING ============================ */
  {
    slug: "link-building",
    category: "Search",
    title: "Link Building & Digital PR",
    metaTitle: "Link Building & Digital PR Austin — Editorial Placements | Optimize Plus",
    metaDescription:
      "White-hat link building and digital PR for Austin brands — editorial placements in Central Texas publishers, industry press, and .edu partners. Real referring domains. No PBNs, no shortcuts, no risk to your Google trust.",
    eyebrow: "Authority · Digital PR",
    hero: {
      headline: "Editorial links Austin brands earn — not the kind Google penalises.",
      deck: "Optimize Plus builds authority for Austin and Texas companies through digital PR, expert commentary, and editorial placements in Austin Business Journal, Austin Chamber, Community Impact, and category-relevant publishers — the kind of links that compound because they were earned, not procured.",
    },
    tldr: [
      "Digital PR campaigns pitched to real editors at real publications",
      "Expert commentary (HARO / Qwoted / Featured) as an always-on flow",
      "Unlinked mention reclamation and broken-link recovery",
      "Guest editorial on category-relevant publications",
      "Monthly referring-domain report with named placements",
    ],
    overview: [
      "The link market has been dominated by shortcuts for 20 years — PBNs, link farms, guest post networks. Every one of them eventually gets deprecated, penalised, or ignored. And the sites that used them go with them.",
      "The links that still matter are the ones a real editor decided to publish. That's why we build authority through digital PR and editorial placements — because those are the links Google trusts and AI models cite.",
    ],
    philosophy: {
      heading: "We only build links we'd be happy to show Google's spam team.",
      body: [
        "Every link we build gets pitched, edited, and approved by a real person at a real publication. That's slower than PBNs and cheaper than paid placements — and it's the only strategy that survives the next algorithm update.",
        "If you're looking for 50 links a month at $80 each, we're the wrong agency. If you're looking for 4–8 placements a month that move the authority needle, we're the right one.",
      ],
    },
    pillars: [
      {
        title: "Digital PR",
        body: "Data-led studies, industry reports, and reactive pitches that give editors a real reason to link.",
        outputs: ["Data-led study production", "Media list per campaign", "Reactive pitch calendar", "Monthly placement report"],
      },
      {
        title: "Expert commentary",
        body: "Your subject-matter experts on the record in category publications — as an always-on programme, not a campaign.",
        outputs: ["HARO / Qwoted / Featured monitoring", "Weekly pitch cycle", "Expert profile setup", "Response template library"],
      },
      {
        title: "Reclamation",
        body: "You already have unlinked brand mentions and broken-link opportunities on relevant sites. We turn them into links.",
        outputs: ["Unlinked mention audit", "Broken-link opportunity list", "Outreach + follow-up flow", "Recovery report"],
      },
      {
        title: "Editorial partnerships",
        body: "Long-term relationships with editors at category publications — the compound moat that makes future placements easier.",
        outputs: ["Editor relationship map", "Category publication list", "Quarterly editorial partnerships review"],
      },
    ],
    framework: {
      name: "The Editorial Trust Stack",
      body: [
        "Every campaign builds trust on three layers: with editors (via useful pitches), with algorithms (via clean links from real sites), and with readers (via genuinely linkable content). Skip a layer and the campaign underperforms.",
      ],
      steps: [
        { label: "Angle", body: "A pitchable angle rooted in your data, expertise, or reactive news." },
        { label: "Asset", body: "The study, quote, or resource an editor can link to." },
        { label: "Outreach", body: "Targeted pitches to editors we've researched, not blast lists." },
        { label: "Report", body: "Every placement named, dated, and linked in your monthly report." },
      ],
    },
    deliverables: [
      { cadence: "Weekly", items: ["Expert commentary pitches", "Reactive PR watch", "Pipeline update"] },
      { cadence: "Monthly", items: ["4–8 editorial placements", "1 data-led study or reactive campaign", "Referring-domain report", "Pipeline review"] },
      { cadence: "Quarterly", items: ["Big-swing PR campaign", "Editorial relationship review", "Competitor backlink gap analysis"] },
    ],
    timeline: [
      { phase: "Foundation", when: "Weeks 1–4", work: ["Expert profile setup", "Angle library", "Editor relationship map", "First-pitch cycle"] },
      { phase: "Build", when: "Months 2–4", work: ["First placements landing", "First data-led study published", "Reclamation programme launched"] },
      { phase: "Compound", when: "Months 5–12", work: ["4–8 placements / mo", "Quarterly big-swing campaign", "Editor partnerships"] },
    ],
    investment: [
      { tier: "Commentary", price: "$3,200 / mo", cadence: "Always-on", includes: ["HARO + Qwoted programme", "2–4 placements / mo", "Monthly report"], best: "Founders and SMEs with a strong point of view." },
      { tier: "Digital PR", price: "$6,500 / mo", cadence: "Always-on + monthly campaign", includes: ["Everything in Commentary", "1 data-led campaign / mo", "4–8 placements / mo"], best: "Brands ready to invest in real category authority." },
      { tier: "PR Scale", price: "$12,500+ / mo", cadence: "Multi-campaign", includes: ["Everything in Digital PR", "Quarterly big-swing campaign", "Dedicated PR lead"], best: "Category leaders competing for share of voice." },
    ],
    fitFor: [
      "Brands with real expertise or data worth pitching",
      "Category challengers building authority from scratch",
      "Teams committed to the 6-month timeline links require",
    ],
    notFor: [
      "Teams looking for 20+ links a month at low cost",
      "Sites with unresolved technical or content issues (fix those first)",
    ],
    relatedCaseStudies: ["foreign-affairs-auto"],
    faqs: [
      { q: "How many links per month?", a: "4–8 quality editorial placements at Growth tier. Fewer than the '50 links' agencies promise, but each one moves Domain Authority in a way theirs don't." },
      { q: "Do you use PBNs?", a: "No. Ever. Every link we build is on a real editorial publication with real traffic and real editors." },
      { q: "What if a link gets removed?", a: "We build the pipeline to replace it. Editorial placements are inherently more durable than paid placements — but nothing is permanent." },
      { q: "How do you measure link quality?", a: "Referring domain authority, topical relevance, editorial nature (not a footer / sitewide link), and click-through traffic. Every placement gets rated in the monthly report." },
      { q: "How long until we see ranking impact?", a: "Referring domain growth impacts rankings on a 60–120 day lag. So a link earned this month typically shows in ranking data by month 3–4." },
    ],
    related: ["seo", "content-strategy", "aeo-geo"],
  },

  /* ============================ GBP ============================ */
  {
    slug: "google-business-profile",
    category: "Local",
    title: "Google Business Profile",
    metaTitle: "Austin Google Business Profile — More Enquiries | Optimize Plus",
    metaDescription:
      "Google Business Profile management in Austin: categories, services, accurate business information and genuine review workflows. Book a Strategy Call.",
    eyebrow: "Local Surface · Austin GBP",
    hero: {
      headline: "Google Business Profile management for Austin businesses.",
      deck: "Make it easier for local customers to understand your services and contact you. We align your profile categories, services, photos, business information and website landing pages, support genuine review workflows and monitor profile interactions without promising a fixed map ranking.",
    },
    tldr: [
      "Category + service audit and recalibration",
      "Service area configuration for multi-location and service-radius businesses",
      "Weekly posting cadence (offers, updates, photos, events)",
      "Review generation and response governance",
      "Suspension prevention and reinstatement",
    ],
    overview: [
      "The average local business treats the GBP as a listing they set up once and forget. The competitive local business treats it as a channel — posting weekly, seeding Q&A, responding to reviews, monitoring competitors.",
      "Profile completeness and clear information help customers evaluate your business. Local visibility also depends on relevance, distance and prominence. We establish a baseline and monitor changes rather than promising that a posting frequency or review count will produce a specific ranking increase.",
    ],
    philosophy: {
      heading: "The GBP is a product. Ship to it weekly.",
      body: [
        "Every week the GBP should get: 1 post, 1–3 new photos, review responses on any new reviews, and a check for competitor changes.",
        "That cadence — plus a clean category / service configuration — is worth more than most on-site local SEO changes for a service-area business.",
      ],
    },
    pillars: [
      { title: "Categories & services", body: "Primary category, secondary categories, and service list — audited and recalibrated for maximum trigger overlap with high-intent queries.", outputs: ["Category audit", "Service list", "Attribute optimisation", "Description rewrite"] },
      { title: "Service areas & radius", body: "For service-area businesses, radius and city list configuration is one of the most under-utilised levers in local SEO.", outputs: ["Service area configuration", "Radius strategy", "Multi-location governance"] },
      { title: "Posts, photos, Q&A", body: "Weekly posting cadence. Geo-tagged photos. Seeded Q&A with owner answers. All monitored and refreshed.", outputs: ["Weekly post calendar", "Photo library", "Q&A seed pack", "Monthly photo refresh"] },
      { title: "Reviews & reputation", body: "Automated review request flow, response templates, and monthly sentiment monitoring.", outputs: ["Review flow (SMS + email)", "Response template library", "Sentiment monitoring", "Bad-review recovery"] },
      { title: "Suspension recovery", body: "GBP suspensions happen — often to legitimate businesses. We handle the appeal, evidence packet, and follow-up.", outputs: ["Reinstatement filing", "Evidence packet", "Follow-up cadence"] },
    ],
    framework: {
      name: "The GBP Weekly Loop",
      body: ["Every week: post, photo, respond, monitor. That's the loop. Skip it and the profile loses momentum."],
      steps: [
        { label: "Post", body: "A GBP post — offer, update, event, or photo." },
        { label: "Photo", body: "1–3 new geo-tagged photos." },
        { label: "Respond", body: "Reply to every new review within 48 hours." },
        { label: "Monitor", body: "Competitor moves, category triggers, suspension signals." },
      ],
    },
    deliverables: [
      { cadence: "Weekly", items: ["GBP post", "Photo refresh", "Review response", "Competitor watch"] },
      { cadence: "Monthly", items: ["Insights report", "Review sentiment analysis", "Q&A seeding sprint", "Suspension risk review"] },
      { cadence: "Quarterly", items: ["Category recalibration", "Photo library refresh", "Reputation deep-dive", "Competitive benchmark"] },
    ],
    timeline: [
      { phase: "Baseline", when: "Weeks 1–2", work: ["Category + service audit", "Attribute optimisation", "Photo baseline", "Review flow setup"] },
      { phase: "Cadence", when: "Weeks 3–8", work: ["Weekly posting", "Q&A seeding", "Review generation launched"] },
      { phase: "Compound", when: "Months 3–6", work: ["Ongoing cadence", "Monthly insights", "Ongoing photo refresh"] },
    ],
    investment: [
      { tier: "Single Location", price: "$950 / mo", cadence: "1 GBP", includes: ["Weekly cadence", "Monthly report", "Review flow"], best: "Single-location businesses ready to treat GBP seriously." },
      { tier: "Multi-Location", price: "$650 / mo per location", cadence: "3+ GBPs", includes: ["Everything in Single", "Cross-location governance", "Consolidated report"], best: "Multi-location operators." },
      { tier: "Reinstatement", price: "$1,800 one-time", cadence: "As needed", includes: ["Appeal packet", "Evidence submission", "Follow-up support"], best: "Suspended profiles needing recovery." },
    ],
    fitFor: [
      "Any business with a GBP that isn't posted to weekly",
      "Multi-location operators with inconsistent local presence",
      "Businesses recovering from a suspension",
    ],
    notFor: [
      "Pure e-commerce brands without a local surface",
      "Businesses unwilling to sustain the weekly cadence",
    ],
    relatedCaseStudies: ["air-tech-pro-hvac", "carage-auto"],
    faqs: [
      { q: "Do GBP posts actually help rankings?", a: "Directly — modestly. Indirectly, they signal an active profile which Google rewards. And they materially improve conversion from map pack impression to call." },
      { q: "How many reviews should we ask for?", a: "Aim for 4–12 new reviews per month depending on your local competition. We benchmark against your top-5 pack competitors." },
      { q: "Can you help a suspended GBP?", a: "Yes. Reinstatement is a specialty. Typical timeline 3 days to 8 weeks depending on the reason and your documentation." },
      { q: "What about GBP for service-area businesses (no storefront)?", a: "GBPs without a storefront are fully supported — you configure service areas instead of a customer-facing address. Proximity still matters, so results skew toward your registered address." },
      { q: "How do reviews impact ranking?", a: "Review count, velocity, and text signals (mentioning services) all influence local pack ranking. Response rate is also a signal." },
    ],
    related: ["local-seo", "seo", "content-strategy"],
  },

  /* ============================ AEO / GEO ============================ */
  {
    slug: "aeo-geo",
    category: "AI Search",
    title: "AI Search Optimization (AEO / GEO)",
    metaTitle: "AI Search Optimization Austin — Get Cited | Optimize Plus",
    metaDescription:
      "AI search optimization in Austin: AEO, GEO and ChatGPT visibility through clear answers, credible entities and citation monitoring. Book a Strategy Call.",
    eyebrow: "AI Citations · AEO / GEO",
    hero: {
      headline: "AI search optimization in Austin: prepare your brand to be cited.",
      deck: "AEO and GEO services for Austin brands seeking visibility in ChatGPT, Perplexity and Google AI Overviews. We improve answer clarity, entity consistency and verifiable source content, then monitor observed citations and referral traffic. AI platforms choose their sources independently; no technique guarantees inclusion.",
    },
    tldr: [
      "Answer-engine schema on every relevant template",
      "Entity signalling for topics, products, people, and locations",
      "Citation-friendly content structures optimised for AI extraction",
      "llms.txt authoring and maintenance",
      "AI citation monitoring via Semrush AI Search + custom tracking",
    ],
    overview: [
      "Customers can now research a business through traditional results, Google AI Overviews or assistants such as ChatGPT and Perplexity. AI search optimization addresses those additional discovery paths without abandoning the crawl, content and authority foundations of SEO.",
      "The playbook for ranking in those surfaces overlaps with SEO but isn't identical. Answer engines cite structured, entity-clear, factually verifiable content — and they cite it disproportionately to the top-3 organic result.",
      "AEO/GEO is the discipline of making sure your content is the answer these engines cite. It's SEO's next-generation companion, not a replacement.",
    ],
    philosophy: {
      heading: "If you're not being cited, you're being replaced.",
      body: [
        "AI answers used to send a click. Increasingly, they replace one. So the goal isn't 'appear as a source' — it's 'be the source the answer is built around'.",
        "That requires content that answers the query directly, in structured formats an LLM can extract cleanly, on a domain whose entity graph the model already trusts.",
      ],
    },
    pillars: [
      { title: "Answer-engine schema", body: "FAQPage, HowTo, Article with author + reviewer, Product with reviews — the schema types LLMs use to identify authoritative sources.", outputs: ["Schema strategy per template", "Author + reviewer entity setup", "Product / Service schema", "Validation via schema.org + GSC"] },
      { title: "Entity signalling", body: "Wikipedia, Wikidata, Crunchbase, LinkedIn — the entity graph LLMs read. We make sure your brand, people, and topics are consistent across it.", outputs: ["Entity graph audit", "Wikidata + Wikipedia review", "About / Organization schema", "Named entity consistency sweep"] },
      { title: "Citation-friendly structure", body: "Direct-answer paragraphs, comparison tables, definitional glossaries — the content structures LLMs extract cleanly.", outputs: ["Content structure templates", "Direct-answer paragraph patterns", "Comparison tables", "Definitional glossary entries"] },
      { title: "llms.txt", body: "The emerging protocol for telling LLMs which content on your site is authoritative and how to cite it.", outputs: ["llms.txt authoring", "Priority page mapping", "Maintenance cadence"] },
      { title: "Citation monitoring", body: "You can't optimise what you don't measure. We track citations in ChatGPT, Perplexity, and AI Overviews monthly.", outputs: ["Semrush AI Search reporting", "Custom prompt tracking", "Citation share of voice", "Monthly citation report"] },
    ],
    framework: {
      name: "The GEO Success Matrix™",
      body: [
        "A four-axis audit and rework framework for AI-answer surfaces: Answer Clarity, Entity Strength, Topical Placement, and Freshness. We don't guess; we score.",
      ],
      steps: [
        { label: "Audit", body: "Score target pages on the four axes of the matrix to identify the binding constraint on citation eligibility." },
        { label: "Structure", body: "Refactor page intro and copy blocks to meet the 'clear answer' requirement (first 100 words)." },
        { label: "Entity", body: "Deploy stable @id schema and Person/Organization graph to clear entity ambiguity for LLMs." },
        { label: "Link", body: "Strengthen cluster placement through targeted internal linking between hub and spoke pages." },
      ],
    },
    deliverables: [
      { cadence: "Weekly", items: ["Prompt monitoring", "New-citation alerts", "AI Overview watch"] },
      { cadence: "Monthly", items: ["Citation share-of-voice report", "3–5 pages GEO-optimised", "Entity graph maintenance", "llms.txt update"] },
      { cadence: "Quarterly", items: ["Deep entity audit", "AI-answer market analysis", "Competitor citation benchmark", "Executive GEO review"] },
    ],
    timeline: [
      { phase: "Baseline", when: "Weeks 1–3", work: ["Entity graph audit", "Schema strategy", "Prompt list + monitoring setup", "llms.txt v1"] },
      { phase: "Build", when: "Weeks 4–10", work: ["Schema rollout", "Content structure refactor", "Direct-answer + FAQ blocks", "First citation gains"] },
      { phase: "Compound", when: "Months 3–6", work: ["Ongoing page GEO-optimisation", "Entity maintenance", "Citation share growth"] },
    ],
    investment: [
      { tier: "GEO Foundation", price: "$4,800 one-time", cadence: "4-week engagement", includes: ["Entity audit", "Schema strategy", "llms.txt", "Prompt monitoring setup"], best: "Teams needing the baseline before an ongoing programme." },
      { tier: "GEO Retainer", price: "$3,900 / mo", cadence: "Ongoing", includes: ["Monthly citation report", "3–5 pages / mo optimised", "Entity maintenance", "llms.txt updates"], best: "Brands actively competing for AI citations." },
      { tier: "GEO + SEO Bundle", price: "From $7,500 / mo", cadence: "Ongoing", includes: ["SEO retainer + GEO layer", "Unified reporting", "Priority citation acquisition"], best: "Brands running both disciplines as one programme." },
    ],
    fitFor: [
      "Brands in categories where AI answers are already appearing",
      "Teams with strong SEO fundamentals ready to compound with GEO",
      "Publishers, SaaS brands, and any category with high-intent research queries",
    ],
    notFor: [
      "Sites without basic technical SEO in place (fix that first)",
      "Brands looking for guaranteed citations — no ethical vendor can promise that",
    ],
    relatedCaseStudies: ["carage-auto"],
    faqs: [
      { q: "How is AEO different from SEO?", a: "SEO optimises for ranking in search results. AEO / GEO optimises for being cited as the source in an AI-generated answer. The disciplines overlap heavily but the scoring criteria differ." },
      { q: "Can you guarantee citations in ChatGPT?", a: "No — and no ethical vendor should. We can measurably grow your citation share, but the models make their own choices per query." },
      { q: "Does GEO cannibalise SEO traffic?", a: "It can reduce click-through on queries where AI Overviews answer directly. It also builds brand authority that lifts SEO on adjacent queries. Net-net, most brands come out ahead." },
      { q: "What's llms.txt?", a: "An emerging convention (similar to robots.txt) telling LLMs which content on your site is authoritative and how to use it. We author and maintain it." },
      { q: "How do you measure citation growth?", a: "Semrush AI Search + custom prompt tracking. We monitor a defined list of category prompts monthly and report your citation share vs. competitors." },
    ],
    related: ["seo", "content-strategy", "technical-seo"],
  },

  /* ============================ ANALYTICS ============================ */
  {
    slug: "analytics-reporting",
    category: "Measurement",
    title: "Analytics & Reporting",
    metaTitle: "SEO Analytics & Reporting Austin — Executive Dashboards | Optimize Plus",
    metaDescription:
      "Austin SEO analytics agency wiring GA4, Google Search Console, Semrush, and Looker Studio into executive-grade dashboards. Monthly reports Austin CFOs actually read. Quarterly business reviews tied to revenue.",
    eyebrow: "Measurement · Analytics",
    hero: {
      headline: "SEO analytics wired so your Austin CFO trusts the report.",
      deck: "GA4, Google Search Console, Semrush, and Looker Studio wired into an executive-grade reporting stack for Austin businesses. Monthly reports a CFO can read in three minutes. Quarterly business reviews that translate rankings into booked revenue for Central Texas operators.",
    },
    tldr: [
      "GA4 configuration and QA (events, conversions, attribution)",
      "Search Console + Semrush pipelines into Looker Studio",
      "Custom executive dashboard per client",
      "Monthly report designed for leadership, not the SEO team",
      "Quarterly business review with revenue attribution",
    ],
    overview: [
      "SEO agencies love ranking reports. Executives don't read them. So the SEO programme gets defunded — not because it isn't working, but because the reporting layer never translated rankings into money.",
      "We build the reporting stack to close that gap. Every metric on the dashboard maps back to a business outcome. Every monthly report opens with the top-line, not the top keyword.",
    ],
    philosophy: {
      heading: "If the CFO can't read it, it doesn't count.",
      body: [
        "Reporting is a product. It has users (leadership, marketing, sales), a hierarchy (top-line, then breakdown), and a job (drive better decisions faster).",
        "We design reports around those constraints. The 60-page 'monthly deck' that most agencies send is theatre; a 3-minute dashboard scan is what leadership actually uses.",
      ],
    },
    pillars: [
      { title: "GA4 configuration", body: "Events, conversions, custom dimensions, attribution modelling — set up cleanly with a QA layer.", outputs: ["Event schema", "Conversion configuration", "GA4 → BigQuery pipe", "QA + monitoring"] },
      { title: "Data pipelines", body: "GSC, Semrush, Ahrefs, call tracking, CRM — piped into a single data layer for cross-source reporting.", outputs: ["Looker Studio connectors", "BigQuery datasets (when scale demands)", "Refresh cadence + monitoring"] },
      { title: "Executive dashboard", body: "One dashboard, three views: leadership (top-line), marketing (channel), SEO (operational). Same data, different lens.", outputs: ["Leadership view", "Marketing view", "SEO ops view", "Alert configuration"] },
      { title: "Monthly report", body: "A short, decision-oriented report designed to be read in three minutes.", outputs: ["Top-line narrative", "Channel breakdown", "Wins + losses + next month", "Anomaly annotations"] },
      { title: "Quarterly business review", body: "The 90-day view: what shipped, what moved, what's next, and what it means for revenue.", outputs: ["QBR deck", "Revenue attribution model", "Roadmap for next quarter"] },
    ],
    framework: {
      name: "The Executive Reporting Standard",
      body: [
        "Every report follows the same structure: top-line result, three-line 'so what', supporting breakdown, next-month commitments. Anything else is optional.",
      ],
      steps: [
        { label: "Top line", body: "The one number leadership needs — organic revenue, MQLs, or bookings." },
        { label: "So what", body: "Three sentences on what changed and why." },
        { label: "Breakdown", body: "The supporting cuts — channel, page type, geography." },
        { label: "Next", body: "The explicit commitments for the next reporting period." },
      ],
    },
    deliverables: [
      { cadence: "Weekly", items: ["Dashboard alerts", "Anomaly annotations"] },
      { cadence: "Monthly", items: ["Monthly executive report", "Dashboard walk-through call", "Attribution reconciliation"] },
      { cadence: "Quarterly", items: ["QBR deck", "Revenue attribution refresh", "Reporting stack audit"] },
    ],
    timeline: [
      { phase: "Setup", when: "Weeks 1–3", work: ["GA4 audit", "Data pipeline build", "Dashboard v1", "QA sweep"] },
      { phase: "Standardise", when: "Weeks 4–6", work: ["First monthly report", "Alert configuration", "Team enablement"] },
      { phase: "Compound", when: "Ongoing", work: ["Monthly reports", "Dashboard refinement", "QBRs"] },
    ],
    investment: [
      { tier: "Reporting Foundation", price: "$5,500 one-time", cadence: "3-week engagement", includes: ["GA4 audit + fix", "Pipeline + dashboard build", "Report template"], best: "Teams with an in-house SEO lead who need the reporting stack." },
      { tier: "Reporting Retainer", price: "$1,900 / mo", cadence: "Ongoing", includes: ["Monthly report", "Dashboard maintenance", "Alert monitoring"], best: "Ongoing reporting layer for any SEO programme." },
      { tier: "Bundled", price: "Included", cadence: "With any retainer", includes: ["Reporting stack is included in all Growth+ engagements"], best: "Clients on Growth or higher tiers." },
    ],
    fitFor: [
      "Teams whose current SEO reporting doesn't reach the CFO",
      "In-house SEO leads who need a reporting infrastructure upgrade",
      "Any Optimize Plus retainer client (reporting is included at Growth+)",
    ],
    notFor: [
      "Brands satisfied with vendor-defaulted PowerPoint reports",
    ],
    relatedCaseStudies: [],
    faqs: [
      { q: "Do you use Looker Studio or something else?", a: "Looker Studio by default. For enterprise volume we use BigQuery + Looker Studio Pro or a client-preferred BI tool." },
      { q: "Do you connect to our CRM?", a: "Yes — HubSpot, Salesforce, and custom CRMs are supported. We pipe qualified conversions back for attribution." },
      { q: "How is this different from a Google Data Studio template?", a: "Templates give you charts. We give you a reporting product with a decision-oriented top-line, alert configuration, and quarterly business reviews." },
      { q: "Can you handle GA4 migration?", a: "Yes — including historical data preservation via BigQuery export where relevant." },
      { q: "Who reads the report?", a: "The report is designed for leadership first (3-minute scan), marketing team second, and SEO ops team third. Same data, three lenses." },
    ],
    related: ["seo", "cro", "aeo-geo"],
  },

  /* ============================ REPUTATION MANAGEMENT ============================ */
  {

    slug: "reputation-management",
    category: "Search",
    title: "Reputation Management & Review Systems",
    metaTitle: "Reputation Management Austin — Local Review Systems | Optimize Plus",
    metaDescription:
      "Austin reputation management agency building automated review generation flows, sentiment monitoring, and LocalBusiness schema for Central Texas service businesses. Win the Map Pack with 5-star proof.",
    eyebrow: "Trust · Local Proof",
    hero: {
      headline: "Austin reputation management that builds a moat of 5-star proof.",
      deck: "Automated review generation, sentiment monitoring, and schema-backed social proof for Austin, Round Rock, and Cedar Park businesses. We turn your fulfillment workflow into a review flywheel that compounds your local pack prominence.",
    },
    tldr: [
      "Automated SMS & email review request flows",
      "Sentiment monitoring and negative-review recovery playbook",
      "LocalBusiness / Service schema review integration",
      "GBP Q&A seeding and reputation monitoring",
      "Monthly sentiment and velocity reporting",
    ],
    overview: [
      "In the local pack, reviews are the primary tiebreaker. Proximity and relevance get you into the consideration set; review velocity and rating get you the click.",
      "Most businesses treat reviews as a byproduct of luck. We treat them as a product surface. We build the systems that ask at the right time, monitor the sentiment in real-time, and project that proof onto your website and GBP.",
    ],
    philosophy: {
      heading: "Review velocity is a ranking signal.",
      body: [
        "Google rewards businesses that are active and trusted. A profile with 500 old reviews loses to a profile with 50 new ones. Velocity matters as much as volume.",
        "We don't just 'manage' your reputation; we engineer the velocity that keeps you at the top of the Austin map pack.",
      ],
    },
    pillars: [
      {
        title: "Review Generation",
        body: "Automated flows that trigger SMS and email requests at the moment of peak customer satisfaction.",
        outputs: ["SMS/Email templates", "Workflow triggers", "In-person QR kits"],
      },
      {
        title: "Sentiment Monitoring",
        body: "Real-time alerts for new reviews across GBP, Yelp, and industry-specific sites.",
        outputs: ["Alert system", "Monthly sentiment report", "Competitor benchmark"],
      },
      {
        title: "Response Playbooks",
        body: "Pre-approved templates for positive and negative reviews that reinforce entity keywords.",
        outputs: ["Response library", "Crisis protocol", "Keyword-rich templates"],
      },
    ],
    framework: {
      name: "The Trust Loop™",
      body: [
        "A four-stage cycle: Capture (at fulfillment), Monitor (across surfaces), Respond (with intent), and Project (via schema).",
      ],
      steps: [
        { label: "Capture", body: "Automated requests triggered by your CRM or payment system." },
        { label: "Monitor", body: "Centralized feed of every mention and review." },
        { label: "Respond", body: "Expert responses that handle feedback and signal relevance." },
        { label: "Project", body: "Review schema that pulls stars into your organic results." },
      ],
    },
    deliverables: [
      { cadence: "Weekly", items: ["New review alerts", "Response drafting", "Velocity check"] },
      { cadence: "Monthly", items: ["Sentiment analysis", "Competitor review scan", "Schema health check"] },
    ],
    timeline: [
      { phase: "Setup", when: "Weeks 1–2", work: ["Audit existing reviews", "Integrate request triggers", "Draft response library"] },
      { phase: "Active", when: "Month 1+", work: ["Launch automated flows", "Weekly response management", "Monthly reporting"] },
    ],
    investment: [
      { tier: "Local Trust", price: "$1,800 / mo", cadence: "Ongoing", includes: ["Automated requests", "Response management", "Monthly reporting"], best: "Single-location service businesses." },
      { tier: "Enterprise Reputation", price: "$4,500+ / mo", cadence: "Ongoing", includes: ["Multi-location monitoring", "Custom CRM integration", "Dedicated analyst"], best: "Multi-location brands and franchises." },
    ],
    fitFor: [
      "Service businesses where local pack visibility is the primary lead driver",
      "Brands with high transaction volume but low review participation",
      "Companies recovering from a period of negative sentiment",
    ],
    notFor: [
      "B2B SaaS with no local intent",
      "Businesses without a Google Business Profile",
    ],
    relatedCaseStudies: ["carage-auto", "foreign-affairs-auto"],
    faqs: [
      { q: "Can you remove bad reviews?", a: "We can report reviews that violate terms of service, but we cannot 'delete' legitimate negative feedback. We focus on burying negatives with a high velocity of new positives." },
      { q: "Does this integrate with my CRM?", a: "Yes, we support most common CRMs and payment processors (ServiceTitan, Housecall Pro, Stripe, Square) to trigger requests automatically." },
    ],
    related: ["local-seo", "google-business-profile", "conversion-rate-optimization"],
  },

  /* ============================ MIGRATION SEO ============================ */
  {
    slug: "migration-seo",
    category: "Foundation",
    title: "SEO Migration Governance",
    metaTitle: "SEO Migration Services Austin — Protect Your Rankings | Optimize Plus",
    metaDescription:
      "Austin SEO migration experts protecting your traffic during redesigns and replatforms. Redirect mapping, site-structure audits, and 60-day post-launch monitoring for Central Texas brands.",
    eyebrow: "Governance · Risk Mitigation",
    hero: {
      headline: "Austin SEO migration governance that protects what you've built.",
      deck: "Redesigns and replatforms are the highest-risk moments in a brand's digital lifecycle. Optimize Plus provides the governance layer — redirect matrices, IA audits, and post-launch monitoring — that ensures not a single click is lost during the move.",
    },
    tldr: [
      "URL-by-URL redirect mapping (old site to new)",
      "Site architecture and IA stress-testing",
      "Pre-launch staging audit and post-launch verification",
      "GSC indexation and crawl-error monitoring",
      "60-day recovery and stabilization program",
    ],
    overview: [
      "Most agencies 'do' redirects. We run migration governance. A redirect file is just a list; governance is the process of ensuring every ranking signal, every internal link, and every canonical relationship is preserved or improved during the cutover.",
      "A failed migration can erase years of work in a weekend. We provide the technical oversight that makes the transition invisible to your rankings.",
    ],
    philosophy: {
      heading: "Migration is a risk-management exercise.",
      body: [
        "You don't 'optimize' during a migration; you defend. The goal is parity on day one, and growth on day thirty.",
        "We prioritize the highest-traffic pages and ensure their link equity is preserved through 1:1 mapping, not generic category redirects.",
      ],
    },
    pillars: [
      {
        title: "Risk Mapping",
        body: "Auditing the legacy site to identify every URL that earns traffic or links.",
        outputs: ["Legacy crawl data", "Priority URL list", "Backlink inventory"],
      },
      {
        title: "Redirect Matrix",
        body: "Granular, page-by-page mapping to the most relevant new destination.",
        outputs: ["Redirect CSV", "Pattern-match rules", "Chain cleanup"],
      },
      {
        title: "Post-Launch Monitoring",
        body: "Real-time tracking of 404s, indexation drops, and ranking volatility.",
        outputs: ["Daily status report", "Fix log", "GSC sync"],
      },
    ],
    framework: {
      name: "The Migration Shield™",
      body: [
        "A multi-layered defensive framework designed to eliminate traffic loss during site transitions. We move beyond simple 301 lists to a deep-data model of entity mapping and link equity preservation.",
      ],
      steps: [
        { label: "Map (Legacy)", body: "Comprehensive audit of all legacy assets—crawls, logs, and link databases—to identify the traffic-driving core." },
        { label: "Model (IA)", body: "Mapping the legacy content to the new information architecture to ensure keyword intent matches exactly." },
        { label: "Map (Redirects)", body: "Creating a granular, one-to-one redirect matrix with pattern-matching fallbacks to capture all long-tail traffic." },
        { label: "Monitor (Cutover)", body: "A 72-hour intensive monitoring phase post-launch to catch 404s, redirect loops, and latency spikes in real-time." },
      ],
    },
    deliverables: [
      { cadence: "Project-based", items: ["Redirect matrix", "Pre-launch audit", "Post-launch verification", "60-day report"] },
    ],
    timeline: [
      { phase: "Planning", when: "Weeks 1–4", work: ["Legacy audit", "Redirect mapping", "IA review"] },
      { phase: "Execution", when: "Launch week", work: ["Pre-launch check", "Cutover monitoring", "Immediate fixes"] },
      { phase: "Stabilization", when: "Months 1–2", work: ["Indexation monitoring", "Search Console cleanup", "Performance review"] },
    ],
    investment: [
      { tier: "Standard Migration", price: "From $8,500", cadence: "Project-based", includes: ["Up to 500 URLs", "Redirect matrix", "30-day monitoring"], best: "SMB sites and small e-commerce stores." },
      { tier: "Enterprise Migration", price: "From $18,000", cadence: "Project-based", includes: ["Unlimited URLs", "Complex IA rebuilds", "90-day monitoring"], best: "Large publications and enterprise platforms." },
    ],
    fitFor: [
      "Brands replatforming (e.g., WordPress to Shopify)",
      "Companies undergoing a major brand redesign",
      "Sites consolidating multiple domains",
    ],
    notFor: [
      "Simple content updates without URL changes",
      "Projects with no staging environment for testing",
    ],
    relatedCaseStudies: ["foreign-affairs-auto"],
    faqs: [
      { q: "Will we lose traffic during a move?", a: "With correct mapping, any drop should be temporary (1–2 weeks) as Google recrawls. Our goal is to recover 100% of traffic within 30 days." },
      { q: "When should we involve an SEO?", a: "At the wireframe stage. If the developer has already built the site, it's often too late to influence the architecture without costly rework." },
    ],
    related: ["technical-seo", "web-design", "ecommerce-seo"],
  },

  /* ============================ WEB DESIGN ============================ */
  {
    slug: "web-design",
    category: "Design",
    title: "Conversion-Centric Web Design",
    metaTitle: "Web Design Austin — Performance-First Marketing Sites | Optimize Plus",
    metaDescription:
      "Austin web design agency building high-performance, conversion-optimized marketing sites. We bridge the gap between aesthetic excellence and technical SEO requirements for Austin brands.",
    eyebrow: "Design · UX Austin",
    hero: {
      headline: "Austin web design that converts traffic into customers.",
      deck: "Performance-first, mobile-optimized, and conversion-centric marketing sites for Austin and Central Texas businesses. We build the digital storefronts that rank on page one and close the deal when users land.",
    },
    tldr: [
      "Custom UI/UX design and design system development",
      "High-performance frontend engineering (100% PageSpeed target)",
      "Conversion Rate Optimization (CRO) baked into the architecture",
      "Content Management System (CMS) setup and training",
      "Full SEO migration governance included",
    ],
    overview: [
      "A beautiful site that doesn't rank is a vanity project. A ranking site that doesn't convert is a waste of money. We build the intersection.",
      "We treat web design as a performance discipline. Every pixel is measured against load time, every layout against mobile accessibility, and every call-to-action against conversion intent.",
    ],
    philosophy: {
      heading: "Design is how it works.",
      body: [
        "We don't start with Photoshop. We start with user intent and site architecture. The visual layer is the final expression of a strategy built for growth.",
        "A site should be as fast as it is beautiful, and as easy to edit as it is to navigate.",
      ],
    },
    pillars: [
      {
        title: "Strategy & IA",
        body: "Mapping the user journey and site structure before a single line is drawn.",
        outputs: ["Sitemap", "Wireframes", "User flow mapping"],
      },
      {
        title: "Performance Engineering",
        body: "Building on modern stacks (React, Next.js, TanStack) for sub-second load times.",
        outputs: ["PageSpeed audit", "Asset optimization", "Core Web Vitals spec"],
      },
      {
        title: "CMS Governance",
        body: "Setup of headless or traditional CMS environments that marketing teams can actually use.",
        outputs: ["CMS configuration", "Editor training", "Content models"],
      },
    ],
    framework: {
      name: "The Growth Architecture™",
      body: [
        "A performance-first design process focusing on sub-second load times and high-intent user journeys: Discover, Design, Build, Launch, and Optimize.",
      ],
      steps: [
        { label: "Discover", body: "Workshops to define brand goals, conversion targets, and technical constraints." },
        { label: "Design", body: "High-fidelity UI and interactive prototypes with a focus on mobile accessibility." },
        { label: "Build", body: "Clean, semantic code built for search engines and human performance (100% CWV)." },
        { label: "Launch", body: "Hardened migration governance and QA sweep to protect existing SEO equity." },
      ],
    },
    deliverables: [
      { cadence: "Project", items: ["Design system", "Site build", "Schema + metadata", "Redirect matrix", "Analytics + monitoring", "60-day post-launch support"] },
    ],

    timeline: [
      { phase: "Discovery", when: "Weeks 1–2", work: ["Brand + audience workshops", "IA + URL strategy", "Framework selection"] },
      { phase: "Design", when: "Weeks 3–6", work: ["Design system", "Key page designs", "Motion + accessibility spec"] },
      { phase: "Build", when: "Weeks 7–14", work: ["Component library", "Template builds", "CMS wiring", "CWV engineering"] },
      { phase: "Launch", when: "Weeks 15–16", work: ["Redirect QA", "Schema validation", "Cutover", "60-day monitoring"] },
    ],
    investment: [
      { tier: "Marketing Site", price: "From $28,000", cadence: "12–16 weeks", includes: ["Up to 20 templates", "CMS", "Schema", "Analytics"], best: "Growing brands needing a professional marketing site." },
      { tier: "Custom Build", price: "From $65,000", cadence: "16–24 weeks", includes: ["Custom templates + integrations", "Full IA rebuild", "Migration governance"], best: "Category leaders and enterprise brands." },
    ],
    fitFor: [
      "Brands where the site is a growth channel, not a brochure",
      "Teams replatforming and wanting SEO governance on the migration",
      "Companies whose current site is holding back organic growth",
    ],
    notFor: [
      "Teams looking for a $5,000 WordPress theme install",
      "Projects without leadership sponsorship for the timeline",
    ],
    relatedCaseStudies: [],
    faqs: [
      { q: "WordPress or Next.js?", a: "Depends on your editing workflow and content model. Most content-heavy marketing sites still fit WordPress well. Apps, headless, or bespoke experiences fit Next.js or Astro better. We recommend based on fit, not fashion." },
      { q: "Do you handle content?", a: "Content strategy and writing is a separate discipline (see Content Strategy). We coordinate closely with content during a build so the site launches populated." },
      { q: "How do you handle migrations?", a: "Redirect matrix mapped page-to-page. Pre-launch QA. 60-day post-launch monitoring. Migration is the single highest-risk moment in SEO — we treat it that way." },
      { q: "Can we edit the site ourselves after launch?", a: "Yes. Every build includes CMS setup and editor training. Marketing teams should never need engineering to publish." },
      { q: "How long from kickoff to launch?", a: "Marketing sites: 12–16 weeks. Custom builds: 16–24 weeks. Rush timelines are available with a scope compromise." },
    ],
    related: ["seo", "technical-seo", "cro"],
  },

  /* ============================ PPC ============================ */
  {
    slug: "ppc",
    category: "Paid",
    title: "PPC & Paid Search",
    metaTitle: "PPC Services Austin — Reach Ready-to-Buy Customers",
    metaDescription:
      "Austin PPC services for Google Ads and Microsoft Ads. Align landing pages, campaign intent and conversion tracking with SEO. Book a Strategy Call.",
    eyebrow: "Paid · PPC Austin",
    hero: {
      headline: "PPC services in Austin, aligned with your sales priorities.",
      deck: "Google Ads, Microsoft Ads and Performance Max management for Austin businesses. We align campaign intent, landing pages, budget allocation and conversion tracking with your organic strategy. Reporting distinguishes paid clicks from qualified enquiries so decisions reflect business outcomes, not traffic alone.",
    },
    tldr: [
      "Google Ads, Microsoft Ads, and Performance Max management",
      "Search + Shopping + Display + YouTube where relevant",
      "Coordinated with SEO so paid + organic don't overlap wastefully",
      "Weekly optimisation, monthly reporting, quarterly strategy review",
      "Conversion tracking + attribution baked in",
    ],
    overview: [
      "Most PPC programmes are managed as an isolated channel. So they end up bidding on branded terms the client already owns organically, and on non-branded terms the client already ranks #1 for. Both are wasted spend.",
      "We manage paid search alongside your organic programme. Paid takes the queries organic doesn't own yet, protects branded from competitors, tests messaging that then flows into SEO copy, and stops the day organic takes over.",
    ],
    philosophy: {
      heading: "Bid on gaps, not on your own rankings.",
      body: [
        "Paid + organic is a portfolio, not two channels. When organic ranks #1 on a term, paid should exit. When organic is on page 2 for a high-intent term, paid should be there filling the gap.",
        "That coordination — impossible when SEO and PPC live in different agencies — is where the compounding happens.",
      ],
    },
    pillars: [
      { title: "Account structure", body: "Campaign, ad group, and keyword architecture designed for match-type control and negative governance.", outputs: ["Account audit", "Structure rebuild", "Negative keyword governance"] },
      { title: "Creative & landing pages", body: "Ad creative and landing page pairing — with A/B testing on both.", outputs: ["Creative library", "Landing page briefs", "Test cadence"] },
      { title: "Conversion tracking", body: "GA4 conversions, offline conversion imports, and CRM-based attribution.", outputs: ["Conversion audit", "Offline conversion pipe", "Attribution model"] },
      { title: "Portfolio governance", body: "Paid + organic overlap review so you're not double-paying for clicks.", outputs: ["Overlap audit", "Bid exit rules", "Cross-channel dashboard"] },
    ],
    framework: {
      name: "The Portfolio Bid Model",
      body: [
        "Every keyword is scored: organic rank, competition, commercial intent, and paid CPA. The score determines whether we bid, exit, or defend.",
      ],
      steps: [
        { label: "Score", body: "Every keyword scored on organic rank + intent + CPA." },
        { label: "Route", body: "Bid, exit, or defend — the rule based on the score." },
        { label: "Measure", body: "Blended organic + paid revenue, not channel-siloed." },
        { label: "Rebalance", body: "Weekly review, quarterly strategy refresh." },
      ],
    },
    deliverables: [
      { cadence: "Weekly", items: ["Bid + budget optimisation", "Negative keyword additions", "Ad copy tests"] },
      { cadence: "Monthly", items: ["Performance report", "Landing page A/B rollout", "Portfolio overlap review"] },
      { cadence: "Quarterly", items: ["Strategy review", "Creative refresh", "Channel expansion evaluation"] },
    ],
    timeline: [
      { phase: "Audit + rebuild", when: "Weeks 1–4", work: ["Account audit", "Structure rebuild", "Conversion tracking QA", "First creative sprint"] },
      { phase: "Optimise", when: "Weeks 5–12", work: ["Weekly optimisation", "A/B testing", "Landing page work"] },
      { phase: "Compound", when: "Ongoing", work: ["Portfolio governance", "Channel expansion", "Quarterly reviews"] },
    ],
    investment: [
      { tier: "Managed PPC", price: "15% of spend · $2,500 min", cadence: "Ongoing", includes: ["Full management", "Weekly optimisation", "Monthly reporting"], best: "Any brand spending $10k+ / mo on paid search." },
      { tier: "Enterprise PPC", price: "10% of spend · $8,000 min", cadence: "Ongoing", includes: ["Everything in Managed", "Dedicated strategist", "Weekly leadership review", "Custom attribution model"], best: "Brands spending $75k+ / mo across paid channels." },
    ],
    fitFor: [
      "Brands running paid search alongside organic",
      "Teams whose current agency treats paid + organic as separate silos",
      "E-commerce brands with Shopping + Performance Max complexity",
    ],
    notFor: [
      "Brands looking to run paid without organic coordination",
      "Sub-$5k monthly spend (agency management overhead outweighs the lift)",
    ],
    relatedCaseStudies: [],
    faqs: [
      { q: "What's your management fee?", a: "15% of ad spend at Managed tier, with a $2,500 minimum. 10% at Enterprise with an $8,000 minimum." },
      { q: "Do you handle Performance Max?", a: "Yes. PMax needs strong feeds and campaign structure to work; we set both up and monitor carefully." },
      { q: "Do you manage social ads?", a: "Not primarily — we're focused on paid search. For Meta / TikTok, we partner with specialists." },
      { q: "How do you coordinate with SEO?", a: "Shared keyword database, weekly overlap review, quarterly portfolio recalibration. Both disciplines see the same dashboard." },
      { q: "Do you write ad copy?", a: "Yes. Ad copy is a creative discipline, not a template exercise. Every account gets fresh creative on a testing cadence." },
    ],
    related: ["seo", "cro", "analytics-reporting"],
  },

  /* ============================ ECOMMERCE ============================ */
  {
    slug: "ecommerce-seo",
    category: "Vertical",
    title: "E-commerce SEO",
    metaTitle: "E-commerce SEO Austin — Shopify, WooCommerce, BigCommerce | Optimize Plus",
    metaDescription:
      "Austin e-commerce SEO agency building for revenue, not sessions. Category page architecture, product schema, faceted navigation governance, and merchandising SEO for Shopify, WooCommerce, BigCommerce, and custom stacks.",
    eyebrow: "Vertical · E-commerce SEO",
    hero: {
      headline: "Austin e-commerce SEO built for revenue, not sessions.",
      deck: "Category architecture, product schema, faceted navigation governance, and merchandising SEO for Austin and Central Texas DTC brands — on Shopify, Shopify Plus, WooCommerce, BigCommerce, and headless stacks like Hydrogen.",
    },
    tldr: [
      "Category (PLP) architecture and internal linking",
      "Product (PDP) schema and content optimisation",
      "Faceted navigation governance (indexation control)",
      "Merchandising SEO: brand + category + collection strategy",
      "Product feed hygiene for Shopping + Performance Max",
    ],
    overview: [
      "E-commerce SEO is different. Most of the traffic (and revenue) sits on category pages, not blog posts. Faceted navigation can multiply indexable URLs by 10,000× if left unchecked. Product pages need schema for rich results and merchandising for conversion.",
      "We run the disciplines that move revenue, not vanity sessions. Category page architecture. PDP schema. Faceted indexation control. Merchandising SEO. And clean coordination with your paid feed programme.",
    ],
    philosophy: {
      heading: "Revenue > sessions.",
      body: [
        "A blog post that ranks for a top-of-funnel term looks great in the dashboard and does nothing for the business. A category page that ranks for a commercial term prints money.",
        "We prioritise the pages that print money, in the order they print it.",
      ],
    },
    pillars: [
      { title: "Category (PLP) architecture", body: "Category hierarchy, URL taxonomy, and internal linking designed to funnel authority to the pages that convert.", outputs: ["Category tree", "URL taxonomy", "PLP content templates", "Internal link rules"] },
      { title: "Product (PDP) schema", body: "Product, Offer, AggregateRating, and Review schema deployed cleanly — for rich results and AI citations.", outputs: ["Schema deployment", "Rich result monitoring", "Review integration"] },
      { title: "Faceted navigation", body: "Filter combinations can create infinite crawl paths. We govern them with rel=nofollow, canonicalisation, or indexation rules.", outputs: ["Faceted audit", "Indexation rules", "Crawl budget analysis"] },
      { title: "Merchandising SEO", body: "Brand pages, collection pages, seasonal landing pages — the merchandising surface built for search.", outputs: ["Brand + collection page briefs", "Seasonal calendar", "Cross-linking rules"] },
      { title: "Feed hygiene", body: "Product feeds for Shopping, PMax, and marketplaces — clean, complete, and aligned to organic.", outputs: ["Feed audit", "Enrichment rules", "Monitoring"] },
    ],
    framework: {
      name: "The Revenue Priority Stack",
      body: [
        "We rank every page by revenue potential (search volume × commercial intent × conversion rate) and work top-down. Top-of-funnel content is scheduled, not prioritised.",
      ],
      steps: [
        { label: "Rank", body: "Every PLP / PDP scored on revenue potential." },
        { label: "Fix", body: "Highest-revenue pages fixed first — schema, content, links." },
        { label: "Expand", body: "Only after top pages are optimised do we add new pages." },
        { label: "Refresh", body: "Quarterly refresh of top-revenue pages." },
      ],
    },
    deliverables: [
      { cadence: "Weekly", items: ["Rank + revenue tracking", "Feed monitoring", "Faceted indexation watch"] },
      { cadence: "Monthly", items: ["3–5 PLPs optimised", "Product schema QA", "Merchandising report"] },
      { cadence: "Quarterly", items: ["Category tree review", "Seasonal planning", "Competitive gap analysis"] },
    ],
    timeline: [
      { phase: "Audit", when: "Weeks 1–3", work: ["Category tree audit", "PDP schema audit", "Faceted crawl audit", "Feed audit"] },
      { phase: "Fix", when: "Weeks 4–10", work: ["Top PLP optimisation", "Schema rollout", "Faceted governance rules"] },
      { phase: "Compound", when: "Months 3–6", work: ["Ongoing PLP work", "Seasonal campaigns", "Cross-channel merchandising"] },
    ],
    investment: [
      { tier: "E-com SEO", price: "$5,800 / mo", cadence: "Ongoing", includes: ["Full programme", "Feed hygiene", "Monthly reporting"], best: "Stores under $5M ARR." },
      { tier: "E-com SEO Enterprise", price: "$12,500+ / mo", cadence: "Ongoing", includes: ["Everything in E-com SEO", "Dedicated strategist", "Custom Looker dashboard"], best: "Stores $5M+ ARR or complex multi-brand catalogues." },
    ],
    fitFor: [
      "Shopify, WooCommerce, BigCommerce, and custom e-commerce stacks",
      "Stores where category pages are the primary revenue driver",
      "DTC brands scaling from $1M to $50M ARR",
    ],
    notFor: [
      "Marketplaces (Amazon, Etsy) — those require a different specialty",
      "Brands looking primarily for content marketing, not commercial SEO",
    ],
    relatedCaseStudies: [],
    faqs: [
      { q: "Do you work with Shopify?", a: "Yes — it's our most common e-commerce stack. We're comfortable with Shopify Plus, Hydrogen (headless), and standard Shopify." },
      { q: "How do you handle faceted navigation?", a: "Depends on the store: rel=nofollow on filter links, canonicalisation to parent categories, or explicit noindex rules — chosen based on crawl budget and competitive positioning." },
      { q: "Do you write product descriptions?", a: "Yes, at scale. We use AI-assisted drafting with human editorial for large catalogues, and full human writing for hero PDPs." },
      { q: "Do you manage Google Merchant Center?", a: "Yes — feed hygiene is part of the programme." },
      { q: "Can you work alongside our paid team?", a: "Yes, and we prefer to. Feed alignment between organic and paid is where a lot of e-commerce revenue leaks." },
    ],
    related: ["seo", "technical-seo", "ppc"],
  },

  /* ============================ SAAS ============================ */
  {
    slug: "saas-seo",
    category: "Vertical",
    title: "SaaS SEO",
    metaTitle: "SaaS SEO Austin — Product-Led Growth Through Search | Optimize Plus",
    metaDescription:
      "Austin SaaS SEO agency building comparison pages, integration pages, product-led content, and category authority for Central Texas B2B SaaS teams. Engineered for MQL, PQL, and pipeline — not vanity traffic.",
    eyebrow: "Vertical · SaaS SEO Austin",
    hero: {
      headline: "Austin SaaS SEO built for MQL and PQL — not just traffic.",
      deck: "Comparison pages, integration pages, product-led content, and category authority — engineered for Austin B2B SaaS teams (Downtown, East Austin, Domain) to grow qualified pipeline through search and get cited by ChatGPT, Perplexity, and Google AI Overviews.",
    },
    tldr: [
      "Comparison and 'vs' page architecture (high-intent bottom-funnel)",
      "Integration and use-case page systems",
      "Product-led content strategy",
      "Category authority via long-form editorial",
      "Coordinated with PLG signup and product-qualified lead tracking",
    ],
    overview: [
      "SaaS SEO looks nothing like local SEO. The moneymaking pages are 'vs' comparisons, integration pages, and use-case pages. The moat is category authority. The conversion event is signup or PQL, not a form fill.",
      "We build the page systems that map to how SaaS buyers actually search — bottom-funnel comparison first, integration pages second, category authority long-form third.",
    ],
    philosophy: {
      heading: "Bottom-funnel first. Always.",
      body: [
        "The order of operations for SaaS SEO is unambiguous: build the bottom-funnel comparison and integration pages first, then the use-case pages, then category authority.",
        "Most SaaS SEO programmes get this backwards — publishing 40 top-of-funnel posts before writing a single 'vs' page. The pipeline never materialises.",
      ],
    },
    pillars: [
      { title: "Comparison pages", body: "'Product A vs Product B' pages targeting exactly the queries your prospects run right before choosing.", outputs: ["Comparison page briefs", "Feature-by-feature matrices", "Fair-play principles"] },
      { title: "Integration pages", body: "Pages for every native and Zapier-adjacent integration — high-intent, low-competition traffic.", outputs: ["Integration page templates", "Feed from product team", "Automated deployment"] },
      { title: "Use-case pages", body: "Persona × job pages — the middle-funnel bridge between category and product.", outputs: ["Persona × use-case matrix", "Landing page briefs", "Internal linking"] },
      { title: "Category authority", body: "Long-form editorial that establishes you as the category authority — the moat that makes 'vs' pages easier to rank.", outputs: ["Category thought leadership calendar", "Data-led studies", "Executive contribution flow"] },
      { title: "PLG signup coordination", body: "SEO metrics coordinated with signup and PQL data, not just sessions.", outputs: ["PQL attribution", "SEO-to-signup dashboard", "Cohort analysis"] },
    ],
    framework: {
      name: "The SaaS Funnel Order",
      body: [
        "Comparison → Integration → Use-case → Category. In that order. Skip the order and the pipeline lags 6+ months behind the traffic.",
      ],
      steps: [
        { label: "Comparison", body: "Bottom-funnel 'vs' pages first — the fastest path to qualified pipeline." },
        { label: "Integration", body: "Every integration a page. High volume, low competition, high intent." },
        { label: "Use-case", body: "Persona × job landing pages — the middle-funnel." },
        { label: "Category", body: "Long-form editorial for authority and refined ranking on the harder terms." },
      ],
    },
    deliverables: [
      { cadence: "Weekly", items: ["1–2 pages published", "PLG signal monitoring"] },
      { cadence: "Monthly", items: ["Comparison + integration page batch", "PQL attribution report", "Category authority update"] },
      { cadence: "Quarterly", items: ["Category positioning review", "Buyer journey remapping", "Executive review"] },
    ],
    timeline: [
      { phase: "Foundation", when: "Weeks 1–4", work: ["Buyer journey mapping", "Comparison + integration inventory", "PQL attribution setup"] },
      { phase: "Bottom funnel", when: "Weeks 5–14", work: ["First 8–12 comparison + integration pages live", "Ranking + signup measurement"] },
      { phase: "Compound", when: "Months 4–9", work: ["Use-case page programme", "Category thought leadership launch"] },
    ],
    investment: [
      { tier: "SaaS SEO", price: "$6,800 / mo", cadence: "Ongoing", includes: ["Full programme", "Content production", "PQL reporting"], best: "SaaS companies from Series A to $50M ARR." },
      { tier: "SaaS SEO Enterprise", price: "$14,500+ / mo", cadence: "Ongoing", includes: ["Everything in SaaS SEO", "Dedicated strategist", "Category thought leadership", "Executive editorial support"], best: "Category-leading SaaS companies at $50M+ ARR." },
    ],
    fitFor: [
      "SaaS companies with a PLG or hybrid go-to-market",
      "Category leaders and challengers competing for share of voice",
      "Product teams open to shipping integration + comparison pages",
    ],
    notFor: [
      "Pre-product-market-fit companies (fix that first)",
      "Highly bespoke enterprise sales cycles where SEO isn't the primary channel",
    ],
    relatedCaseStudies: [],
    faqs: [
      { q: "Do you write comparison pages?", a: "Yes — with a fair-play principle. Comparisons that misrepresent competitors backfire; ours don't." },
      { q: "How many integration pages do we need?", a: "One per integration, minimum. Most SaaS companies have 20–100 integrations they're not exposing to search." },
      { q: "How do you measure success?", a: "SEO-attributed signups and PQLs — not sessions. We wire the dashboard to your product analytics." },
      { q: "Do you work with pre-product companies?", a: "No — we recommend focusing on product-market fit first. SEO compounds; but it doesn't compound off nothing." },
      { q: "Can you interview our product team?", a: "Yes — and we insist on it. Category authority requires actual expertise. We interview PMs, engineers, and customer-facing teams to source that expertise." },
    ],
    related: ["seo", "content-strategy", "aeo-geo"],
  },

  /* ============================ DIGITAL PR ============================ */
  {
    slug: "digital-pr",
    category: "Authority",
    title: "Digital PR & Link Building",
    metaTitle: "Digital PR Agency Austin, TX — Earned Media & Editorial Backlinks | Optimize Plus",
    metaDescription:
      "Austin digital PR agency earning editorial coverage in Texas Monthly, Austin Business Journal, Community Impact, Built In Austin, and national outlets. Data-led story angles that pull authority links and brand mentions the algorithm actually rewards.",
    eyebrow: "Authority · Digital PR",
    hero: {
      headline: "Earn the coverage that Google — and Central Texas buyers — actually trust.",
      deck: "Digital PR treated as an earned-media discipline, not a link-buying transaction. We build newsroom-quality data stories and pitch them into Texas Monthly, Austin Business Journal, Community Impact, Built In Austin, and the national outlets your buyers already read.",
    },
    tldr: [
      "Newsroom-grade data studies and survey stories",
      "Journalist relationships across Texas and national beats",
      "HARO / Qwoted / Featured expert commentary programme",
      "Founder & executive thought-leadership placement",
      "Reactive newsjacking for time-sensitive angles",
    ],
    overview: [
      "Google's link graph is now dominated by editorial signals from real newsrooms. Guest posts on farmed domains have been quietly devalued for years. What still moves the needle is a mention in a publication with an actual masthead and an actual audience.",
      "Our digital PR programme is built around data — surveys, first-party datasets, public-records analysis — turned into stories journalists in Austin, Dallas, Houston, and national beats want to write. Every campaign is measured by placements, referral traffic, and organic ranking lift on the pages the coverage points to.",
    ],
    philosophy: {
      heading: "Story first. Link second.",
      body: [
        "Journalists don't care about your backlink target. They care about a story that will get read. We start every campaign by asking 'what would a Texas Monthly staff writer actually cover?' — and only then reverse-engineer the assets and angles.",
        "The link is the byproduct of a genuinely useful story, not the pitch. That's why our placement rates run 4–7× higher than agencies pitching generic 'expert roundup' link bait.",
      ],
    },
    pillars: [
      {
        title: "Data studies",
        body: "First-party surveys, dataset analysis, and public-records deep dives that give journalists a headline they can't get elsewhere.",
        outputs: ["1–2 data studies per quarter", "Chart-ready dataset", "Press release + methodology page", "Embargoed pitch calendar"],
      },
      {
        title: "Reactive PR & newsjacking",
        body: "Rapid-response commentary programme that gets your executives quoted in trending Texas and national stories within hours.",
        outputs: ["HARO / Qwoted / Featured monitoring", "Executive quote library", "24-hour response SLA", "Placement log"],
      },
      {
        title: "Thought leadership",
        body: "Bylined columns for founders and subject-matter experts in Austin Business Journal, Built In Austin, and vertical trade press.",
        outputs: ["Editorial calendar", "Ghost-drafted columns", "Editor relationship management"],
      },
      {
        title: "Local authority",
        body: "Austin-specific angles — 'Austin housing affordability index', 'Central Texas HVAC repair costs by ZIP' — that anchor the brand as a Texas expert.",
        outputs: ["Austin-native data hooks", "Community Impact & Austonia pitches", "Chamber & association placements"],
      },
    ],
    framework: {
      name: "The Newsroom Loop",
      body: [
        "Digital PR fails when treated as one-off outreach. It compounds when treated as a rolling newsroom that ships a story every 2–3 weeks, monitors what lands, and doubles down on the angles journalists reward.",
      ],
      steps: [
        { label: "Story mine", body: "Interview execs, mine internal data, and scan trending beats for angles worth telling." },
        { label: "Package", body: "Turn each angle into a data study, press release, and pitch kit with visuals ready to publish." },
        { label: "Pitch", body: "Targeted, personalised outreach to Texas + national beat reporters — never mass blasts." },
        { label: "Measure", body: "Track placements, DR/traffic of each domain, referral clicks, and downstream ranking lift." },
      ],
    },
    deliverables: [
      { cadence: "Monthly", items: ["1 hero campaign", "Reactive PR quotes (5–10)", "Placement + link report"] },
      { cadence: "Quarterly", items: ["1 flagship data study", "Media relationship map", "Domain authority review"] },
    ],
    timeline: [
      { phase: "Ramp", when: "Weeks 1–4", work: ["Story audit + angle backlog", "Journalist database build (Cision / Muck Rack)", "First reactive quotes shipped"] },
      { phase: "Publish", when: "Weeks 5–12", work: ["First hero campaign live", "5–15 placements landed", "Reactive PR cadence stable"] },
      { phase: "Compound", when: "Month 4+", work: ["Quarterly data studies", "Named-expert authority builds", "Repeat-source relationships with Texas beats"] },
    ],
    investment: [
      { tier: "Digital PR Retainer", price: "$6,500 / mo", cadence: "Ongoing", includes: ["1 hero campaign / mo", "Reactive PR", "Placement reporting"], best: "Brands with a real expert bench and $50k+ ACV." },
      { tier: "Data Study Campaign", price: "$14,500 one-time", cadence: "6-week sprint", includes: ["Full data study", "Press kit", "Targeted pitch to 60+ journalists"], best: "Brands with a defensible dataset and a launch moment." },
    ],
    fitFor: [
      "Founders or execs willing to speak to journalists",
      "Brands with a defensible dataset, POV, or original research",
      "Companies where domain authority is the ranking bottleneck",
    ],
    notFor: [
      "Teams looking to buy links or 'sponsored posts'",
      "Brands unwilling to commit executive time to interviews",
      "Businesses that want guaranteed link counts per month",
    ],
    relatedCaseStudies: [],
    faqs: [
      { q: "How is this different from link building?", a: "Traditional link building targets a link. Digital PR targets a story that a real journalist wants to publish — the link comes along for the ride. Google's algorithm can tell the difference, and the SEO impact is materially larger." },
      { q: "How many placements will we get per month?", a: "A healthy programme lands 4–10 editorial placements per month across reactive commentary and hero campaigns. Volume varies with the strength of the underlying dataset and executive availability." },
      { q: "Do you guarantee links?", a: "No — and any agency that does is buying them, which is a Google policy violation. We guarantee the campaign work; the placements are earned." },
      { q: "Can you help with crisis PR?", a: "Reactive commentary and reputation-adjacent PR are in scope. Full crisis-communications counsel isn't — we'll refer you to a specialist crisis firm when needed." },
      { q: "How do you measure ROI?", a: "Placements landed, referring domain quality (DR, traffic), referral clicks, and organic ranking lift on the pages the coverage points to. Reported monthly with sourced screenshots." },
    ],
    related: ["link-building", "content-strategy", "seo"],
  },

  /* ============================ REPUTATION MANAGEMENT ============================ */
  {
    slug: "reputation-management",
    category: "Local",
    title: "Reputation & Review Management",
    metaTitle: "Reputation Management Austin, TX — Reviews, Ratings & Local Trust | Optimize Plus",
    metaDescription:
      "Austin reputation management agency systematising review generation, response, and monitoring across Google, Yelp, industry directories, and AI answer engines. Rebuild trust signals that move Local Pack rankings and close rates.",
    eyebrow: "Local · Reputation",
    hero: {
      headline: "Turn reviews into a compounding ranking and conversion asset.",
      deck: "Reviews are now a top-3 Local Pack ranking signal and the single strongest close-rate lever on any local business landing page. We build the systems that generate them, respond to them, and defend against the ones that shouldn't be there.",
    },
    tldr: [
      "Automated review generation flows tied to your CRM / DMS / PMS",
      "Response SLAs and templates across Google, Yelp, Facebook, industry sites",
      "Review-schema markup so stars show in SERP and AI answers",
      "Legitimate removal requests for policy-violating reviews",
      "Monthly sentiment reports and competitor benchmarking",
    ],
    overview: [
      "Most Austin businesses ask for reviews sporadically and respond to negatives defensively. Both are wrong. The businesses winning the Local Pack ask on a schedule and respond to every review — positive, negative, and neutral — with a documented voice and SLA.",
      "We design the review request timing per your customer lifecycle, wire it into your CRM or point-of-sale, publish response templates your team can actually use, and monitor the platforms that matter for your vertical — including the AI answer engines that now surface review sentiment in their responses.",
    ],
    philosophy: {
      heading: "Systematise generation. Humanise response.",
      body: [
        "Review generation should be automated. Review response should sound like a human — because it will be read by future customers, not just the reviewer.",
        "We build the automation, write the response voice guide, and train your team so responses go out within 24 hours without sounding templated.",
      ],
    },
    pillars: [
      {
        title: "Generation systems",
        body: "Trigger-based review requests fired from your CRM at the exact right moment — post-service for HVAC, post-appointment for dental, post-delivery for e-commerce.",
        outputs: ["Trigger map", "SMS + email templates", "QR + kiosk fallback assets", "Weekly volume dashboard"],
      },
      {
        title: "Response programmes",
        body: "Voice guide, response templates by scenario, escalation ladder, and a 24-hour SLA on every review across every platform.",
        outputs: ["Response voice guide", "Template library", "Escalation ladder", "SLA dashboard"],
      },
      {
        title: "Monitoring & removals",
        body: "Continuous monitoring across Google, Yelp, Facebook, and vertical directories (Healthgrades, Avvo, Angi, BBB). Legitimate removal requests for policy-violating reviews.",
        outputs: ["Alert configuration", "Weekly monitoring report", "Removal request programme", "Sentiment trend"],
      },
      {
        title: "Schema & syndication",
        body: "Review schema on service and location pages, aggregate rating in SERP, and syndication of testimonial content across landing pages.",
        outputs: ["Review + AggregateRating schema", "Testimonial page system", "Rich-result monitoring"],
      },
    ],
    framework: {
      name: "The Trust Flywheel",
      body: [
        "Every stage feeds the next. More requests → more reviews → higher star average → better Local Pack rank → more customers → more review requests. Break the flywheel at any stage and the compounding stops.",
      ],
      steps: [
        { label: "Ask", body: "Automated request at the right moment in the customer lifecycle." },
        { label: "Respond", body: "24-hour SLA on every review across every platform." },
        { label: "Amplify", body: "Best reviews syndicated to landing pages via schema and testimonial modules." },
        { label: "Defend", body: "Monitor, escalate, and remove policy-violating reviews." },
      ],
    },
    deliverables: [
      { cadence: "Weekly", items: ["Review responses (all platforms)", "New-review report", "Alerting review"] },
      { cadence: "Monthly", items: ["Sentiment analysis", "Competitor benchmarking", "Removal request batch"] },
    ],
    timeline: [
      { phase: "Setup", when: "Weeks 1–3", work: ["CRM / POS integration", "Trigger + template design", "Voice guide + escalation ladder"] },
      { phase: "Launch", when: "Weeks 4–6", work: ["Generation flows live", "Response SLA in effect", "First monitoring cycle"] },
      { phase: "Compound", when: "Month 2+", work: ["Volume + rating trending up", "Removals in progress", "Schema surfaced in SERP"] },
    ],
    investment: [
      { tier: "Reputation Foundation", price: "$4,200 one-time", cadence: "6-week setup", includes: ["Trigger + template design", "Voice guide", "Schema + syndication"], best: "Businesses without an existing review programme." },
      { tier: "Reputation Retainer", price: "$2,400 / mo per location", cadence: "Ongoing", includes: ["Response management", "Monitoring", "Monthly sentiment report"], best: "Multi-location or high-review-volume brands." },
    ],
    fitFor: [
      "Multi-location Austin & Texas service businesses",
      "Healthcare, legal, home services, automotive, hospitality",
      "Any brand where review volume or rating lags competitors",
    ],
    notFor: [
      "Brands looking to buy fake reviews (Google will catch it; we won't help)",
      "Businesses unwilling to fix the underlying service issues driving negative reviews",
    ],
    relatedCaseStudies: [],
    faqs: [
      { q: "Can you remove negative reviews?", a: "Only reviews that violate the platform's policy — spam, off-topic, conflict of interest, TOS violations. We can't remove legitimate negative feedback, but we can help you respond in a way that preserves trust with future readers." },
      { q: "How fast do reviews influence rankings?", a: "Google's Local Pack typically reflects review velocity changes within 4–8 weeks. Star average and total count both matter — velocity often matters more than either." },
      { q: "Do AI answer engines use reviews?", a: "Yes. ChatGPT, Perplexity, and Google's AI Overviews all summarise review sentiment in their answers about local businesses. We optimise for both traditional SERP and AI answer surfaces." },
      { q: "What platforms do you cover?", a: "Google (primary), Yelp, Facebook, and the top vertical directory for your industry (Healthgrades, Avvo, Angi, BBB, Cars.com, TripAdvisor, etc.)." },
      { q: "Do you write fake reviews?", a: "Never. It violates every platform's TOS, Google will detect it, and it destroys the exact trust signal you're trying to build." },
    ],
    related: ["local-seo", "google-business-profile", "seo"],
  },

  /* ============================ MIGRATION SEO ============================ */
  {
    slug: "migration-seo",
    category: "Foundation",
    title: "SEO Migration & Replatforming",
    metaTitle: "SEO Migration Agency Austin, TX — Replatforming, Redesigns & Domain Moves | Optimize Plus",
    metaDescription:
      "Austin SEO migration agency de-risking replatforms, redesigns, and domain moves. Comprehensive redirect maps, parity audits, and post-launch monitoring so you keep the traffic you spent years earning.",
    eyebrow: "Foundation · Migration",
    hero: {
      headline: "Replatform, redesign, or move domains — without losing the traffic you already earned.",
      deck: "Most migration traffic losses aren't caused by Google — they're caused by missing redirects, hero-page rewrites that lose keyword targeting, or unhandled parameters. We run the pre-launch parity audit, ship the redirect map, and monitor the cutover so nothing quietly disappears.",
    },
    tldr: [
      "Pre-migration audit: URL inventory, ranking pages, revenue-attributed URLs",
      "1-to-1 redirect map covering every indexed URL and top backlinks",
      "Content parity check: on-page elements, schema, internal links preserved",
      "Staging QA, cutover checklist, and post-launch monitoring plan",
      "Recovery playbook if metrics dip in the first 8 weeks",
    ],
    overview: [
      "Every replatform, redesign, or domain move is a controlled explosion. Done well, the site emerges faster, cleaner, and better-ranking. Done poorly, six years of SEO equity evaporates in a single Tuesday-morning cutover.",
      "We've run migrations across Shopify → Shopify Plus, WordPress → Webflow, custom → Next.js, sub-domain → sub-directory, and full domain rebrands. The pattern that wins every time is the same: exhaustive pre-audit, complete redirect map, content parity, staged QA, and a 90-day monitoring plan with a rollback trigger.",
    ],
    philosophy: {
      heading: "The migration is the small part. The audit is the big part.",
      body: [
        "By the time you're cutting over DNS, the SEO work should already be done. The audit — every URL, every ranking, every backlink target, every schema block, every internal link — is where 90% of migration risk lives.",
        "We treat cutover day as an operational checklist, not a strategic exercise. If a decision has to be made on cutover day, we've already failed the audit.",
      ],
    },
    pillars: [
      {
        title: "Pre-migration audit",
        body: "Complete inventory of every URL, ranking, backlink target, and revenue-attributed page. Sourced from GSC, Semrush, Ahrefs, GA4, and a full crawl.",
        outputs: ["URL inventory (indexed + backlinked)", "Ranking + traffic baseline", "Backlink target map", "Revenue-attributed URL list"],
      },
      {
        title: "Redirect strategy",
        body: "1-to-1 redirect map for every URL that ranks, receives backlinks, or drives revenue. Chained redirects flattened. Query parameters handled explicitly.",
        outputs: ["Full redirect map (CSV + regex)", "Chain flattening script", "Parameter handling rules", "301 vs 302 decision log"],
      },
      {
        title: "Content & schema parity",
        body: "Every page's title, H1, on-page content, internal links, and schema preserved or intentionally improved — never accidentally lost.",
        outputs: ["Parity diff report", "Schema preservation checklist", "Internal link migration plan", "Hero-page rewrite review"],
      },
      {
        title: "Cutover & monitoring",
        body: "Staged QA on a real environment, cutover-day operational checklist, and 90 days of daily monitoring with defined rollback triggers.",
        outputs: ["Staging QA sign-off", "Cutover checklist", "90-day monitoring dashboard", "Rollback trigger definition"],
      },
    ],
    framework: {
      name: "The Migration Shield™",
      body: [
        "A multi-layered defensive framework designed to eliminate traffic loss during site transitions. We move beyond simple 301 lists to a deep-data model of entity mapping and link equity preservation.",
      ],
      steps: [
        { label: "Map (Legacy)", body: "Comprehensive audit of all legacy assets—crawls, logs, and link databases—to identify the traffic-driving core." },
        { label: "Model (IA)", body: "Mapping the legacy content to the new information architecture to ensure keyword intent matches exactly." },
        { label: "Map (Redirects)", body: "Creating a granular, one-to-one redirect matrix with pattern-matching fallbacks to capture all long-tail traffic." },
        { label: "Monitor (Cutover)", body: "A 72-hour intensive monitoring phase post-launch to catch 404s, redirect loops, and latency spikes in real-time." },
      ],
    },
    deliverables: [
      { cadence: "Pre-launch", items: ["Baseline audit", "Redirect map", "Parity report", "Staging QA sign-off"] },
      { cadence: "Cutover", items: ["Operational checklist", "Real-time monitoring", "War-room support"] },
      { cadence: "Post-launch", items: ["Daily monitoring (30 days)", "Weekly review (60 days)", "Recovery playbook if triggered"] },
    ],
    timeline: [
      { phase: "Audit & map", when: "Weeks 1–4", work: ["Baseline capture", "URL inventory", "Redirect map v1"] },
      { phase: "Build & stage", when: "Weeks 5–8", work: ["Content parity checks", "Schema preservation", "Staging QA"] },
      { phase: "Cutover", when: "Week 9", work: ["War-room support", "Real-time monitoring", "Sign-off on 24-hour metrics"] },
      { phase: "Monitor", when: "Weeks 10–20", work: ["Daily → weekly monitoring", "Redirect gap fixes", "Ranking recovery playbook"] },
    ],
    investment: [
      { tier: "Migration Package", price: "from $18,500", cadence: "8–12 week project", includes: ["Full audit + redirect map", "Parity checks", "Cutover war-room", "90-day monitoring"], best: "Any replatform, redesign, or domain move above 5,000 monthly organic sessions." },
      { tier: "Enterprise Migration", price: "custom", cadence: "12–20 week project", includes: ["Multi-site consolidation", "International hreflang migration", "Dedicated engineering pair"], best: "Multi-domain, multi-language, or 6-figure-monthly-traffic migrations." },
    ],
    fitFor: [
      "Sites replatforming (Shopify, WordPress, Webflow, Next.js, custom)",
      "Full redesigns changing URL structure",
      "Domain moves, rebrands, or multi-site consolidations",
    ],
    notFor: [
      "Teams unwilling to delay launch for staging QA",
      "Sites without analytics or GSC access for baseline capture",
    ],
    relatedCaseStudies: [],
    faqs: [
      { q: "How much traffic loss is normal after a migration?", a: "None. Well-executed migrations show zero loss and often a lift within 30–60 days as technical debt is retired. Loss is a symptom of missing redirects, changed content, or lost internal links — all preventable." },
      { q: "Can you migrate an already-launched site that lost traffic?", a: "Yes — this is the emergency variant. We run the same audit against the new site, identify redirect gaps and content regressions, and ship the fixes. Recovery is possible but takes 60–120 days." },
      { q: "Do we need to freeze the site during migration?", a: "Content freezes help but aren't mandatory. We can accommodate ongoing publishing with a defined merge protocol." },
      { q: "What's your rollback trigger?", a: "A defined threshold on organic clicks, impressions, or revenue over a 7-day window. Threshold is set jointly with your team before cutover." },
      { q: "How does this integrate with our dev team?", a: "We ship the redirect map, parity checklists, and monitoring dashboards. Your dev team owns the deployment. We're in the war room for cutover." },
    ],
    related: ["technical-seo", "seo", "web-design"],
  },
];

export const getService = (slug: string) => SERVICES.find((s) => s.slug === slug);

export const getRelatedServices = (slug: string) => {
  const s = getService(slug);
  if (!s) return [];
  return s.related.map(getService).filter(Boolean) as ServicePage[];
};
